import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import {
  Wifi, WifiOff, Radio, Map as MapIcon, Calendar, RefreshCw,
  Settings, Zap, AlertCircle, Terminal, Plus, Trash2, Building2,
  Activity, Sprout, Thermometer, Droplets, Waves, ToggleLeft, Pencil, Upload,
  Clock, ClockAlert, CircleSlash, Cpu,
} from 'lucide-react';
import { useOrgTimezone, useOrgTimezoneOrNull, formatDateTimeInTz, formatTimeInTz } from '@/utils/timezone';
import { siteOffsetSec } from '@/utils/irrigationClock';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import PageHeader from '../../components/PageHeader';
import EspNowScheduleBuilder from '../../components/irrigation/EspNowScheduleBuilder';
import RainSkipPanel from '../../components/irrigation/RainSkipPanel';
import EspNowLiveMap from '../../components/irrigation/EspNowLiveMap';
import orchardService from '../../services/orchard';
import espnowService, {
  type EspNowController,
  type EspNowRouter,
  type MasterPump,
  type ScheduleProgram,
  type EspNowEvent,
  type FieldCommand,
  type NodeScheduleState,
  type RelayScheduleState,
  type LiveMapData,
  type ControllerSensorData,
  type ConfigAckPayload,
  CONFIG_ACK_STATUS,
  getRelayState,
  relayCountFor,
  usableRelays,
  formatSecsOfDay,
  formatCountdown,
  formatDuration,
  clockSkewSec,
} from '../../services/espnowIrrigation';
import sensorDevicesService, { type SensorType, type SensorChannel } from '../../services/sensorDevices';
import { useAppRefresh } from '../../hooks/useAppRefresh';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { isOrgAdmin, isOrgSuperAdmin } from '../../utils/auth';
import { errorMessage } from '../../utils/apiResponse';
import '../../styles/GlobalPageStyles.css';
import { nodeNoun } from '../../utils/nodeLabel';
import { localDaySec } from '../../utils/irrigationRuns';
import { buildLiveMapStatus, type ValveStatus } from '../../utils/liveMapStatus';
import {
  relayTile,
  relayCommandBusy,
  relayIntendedState,
  withScheduledTone,
  type RelayCommand,
  type RelayTone,
} from '../../utils/relayCommand';
import MeshDiagnostics from '../../components/irrigation/MeshDiagnostics';
import RecentActivity from '../../components/irrigation/RecentActivity';
import NodeEnrolment from '../../components/irrigation/NodeEnrolment';
import NodeSelfTest from '../../components/irrigation/NodeSelfTest';
import BridgeUpdatePanel from '../../components/irrigation/BridgeUpdatePanel';
import StopWateringButton from '../../components/irrigation/StopWateringButton';
import WateringHoldBanner from '../../components/irrigation/WateringHoldBanner';
import useWateringStop from '../../hooks/useWateringStop';

type Tab = 'routers' | 'schedules' | 'map' | 'events' | 'sensors' | 'settings';

const TABS: Tab[] = ['routers', 'schedules', 'map', 'events', 'sensors', 'settings'];

// Manual relay taps are for testing a valve or pump, nothing more. One tap turns
// it on, the next tap sends CLEAR_RELAY and stops it — but the SET_RELAY window
// is deliberately short so a mistaken tap, or one nobody comes back to, cannot
// leave water running. The tap-again path is the normal way to stop; this is
// only the backstop.
const MANUAL_RUN_MAX_SEC = 60;
const MANUAL_RUN_MAX_LABEL = '60 seconds';

// How long a tap waits for the SITE to say it carried the command out. The
// bridge collects its outbox about every 15 s and retries a node up to five
// times before giving up, so an honest refusal can take most of a minute to
// arrive — and a refusal is the answer most worth waiting for.
const RELAY_ACK_WAIT_MS = 60_000;
// And then for the NODE to report the output. The bridge drains its outbox at
// the start of a poll tick and runs commands at the end of one, so a reading
// reaches the server a tick after the ack announcing it.
const RELAY_READ_WAIT_MS = 60_000;
const RELAY_POLL_MS = 3_000;

const sleep = (ms: number) => new Promise(res => setTimeout(res, ms));

/**
 * One colour per claim, and the claims are ranked by who is making them.
 *
 * Green is the node's own word: it reported this output on. Amber is ours — we
 * asked and the site says it obliged, which is not the same thing and never was.
 * Red is a refusal or a contradiction. Grey is off, on the same evidence green
 * is on.
 *
 * A master pump keeps its identity in the BORDER rather than the fill, so the
 * fill is free to mean one thing everywhere.
 */
const RELAY_TONE: Record<RelayTone, string> = {
  on: 'bg-green-500 text-white hover:bg-green-600',
  commanded: 'bg-amber-400 text-white hover:bg-amber-500',
  failed: 'bg-red-500 text-white hover:bg-red-600',
  busy: 'bg-gray-100 border border-gray-300 text-gray-500',
  off: 'bg-gray-50 border border-gray-200 text-gray-500 hover:bg-gray-100',
};

export default function EspNowControllerDetail() {
  const { controllerId } = useParams<{ controllerId: string }>();
  const orgTimezone = useOrgTimezone();
  const orgTzOrNull = useOrgTimezoneOrNull();
  const { user } = useAuth();
  // super_admin still gates the OTA firmware-update buttons (the backend
  // enforces super_admin on POST /espnow/controllers/:id/ota).
  const isSuperAdmin = isOrgSuperAdmin(user);
  // Matches the endpoint's own gate (irrigationAdmin = admin or super_admin), so
  // the button is not offered to somebody the server will refuse.
  const isAdmin = isOrgAdmin(user);
  // irrigation.admin seat holders can change farm assignment, set up the
  // controller's sensor device, and manage sensor channels. Read access is
  // available to any user on the irrigation subscription.
  const { hasAccess: canIrrigationAdmin } = useModuleLicense('irrigation', 'admin');
  // Stopping needs no seat beyond the module — whoever can see that something
  // is wrong has to be able to stop it. Resuming needs the admin seat, which is
  // what the endpoint enforces; the banner says so rather than going blank.
  const wateringStop = useWateringStop(controllerId);
  const [controller, setController] = useState<EspNowController | null>(null);
  // 'Node' on XBee sites, 'Router' on ESP-NOW - see utils/nodeLabel.
  const noun = nodeNoun(controller?.transport);
  const [routers, setRouters] = useState<EspNowRouter[]>([]);
  const [masterPumps, setMasterPumps] = useState<MasterPump[]>([]);
  const [programs, setPrograms] = useState<ScheduleProgram[]>([]);
  const [events, setEvents] = useState<EspNowEvent[]>([]);
  // What we asked the site to do, and what it answered. Sits beside the
  // events because the two halves of a diagnosis were split: the events say
  // what the site did on its own, the commands say what it did with us.
  const [commands, setCommands] = useState<FieldCommand[]>([]);
  // What each node reports it is actually holding (§3.7). Distinct from the
  // programs above, which are only what the server intended to send.
  const [scheduleState, setScheduleState] = useState<NodeScheduleState[]>([]);
  // The tab lives in the URL so the farm page can link straight at one: its
  // map preview opens ?tab=map, a schedule row opens ?tab=schedules&program=X.
  // Routers stays the bare-URL default, so existing links are unaffected.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as Tab | null;
  const tab: Tab = tabParam && TABS.includes(tabParam) ? tabParam : 'routers';
  const focusProgramId = searchParams.get('program');
  const setTab = useCallback((next: Tab) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'routers') params.delete('tab');
    else params.set('tab', next);
    // The focused program belongs to the link that arrived, not to the tab the
    // reader moves on to.
    if (next !== 'schedules') params.delete('program');
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [statusRefreshing, setStatusRefreshing] = useState(false);
  const [timeSyncing, setTimeSyncing] = useState(false);
  const [otaSending, setOtaSending] = useState<string | null>(null); // 'master' or router_mac
  const [farms, setFarms] = useState<Array<{ id: number; name: string }>>([]);
  const [savingFarm, setSavingFarm] = useState(false);
  // Every manual tap still being followed, keyed `${routerMac}:${relayIndex}`,
  // from the POST through the site's acknowledgement to the node's own reading
  // of the output. See utils/relayCommand — the two are different claims and
  // the tile colours them differently.
  const [relayCommands, setRelayCommands] = useState<Map<string, RelayCommand>>(new Map());
  // One token per relay, bumped on every tap. A tap that is superseded — the
  // operator stopping a 60 s test run early, say — must not have its trailing
  // read-back write a verdict over the newer tap's.
  const relayRun = useRef<Map<string, number>>(new Map());
  // The bridge's own view of today's programs, held with the moment it was read
  // so its `now_sec` can be advanced by wall clock between polls. Only fetched
  // for sites whose timers the bridge holds — see `bridgeTimed` below.
  const [liveMap, setLiveMap] = useState<{ data: LiveMapData; at: number } | null>(null);

  const load = useCallback(async () => {
    if (!controllerId) return;
    try {
      const [ctrls, rts, pumps, progs, evts, cmds, sched, farmsRes] = await Promise.all([
        espnowService.getControllers(),
        espnowService.getRouters(controllerId).catch(() => [] as EspNowRouter[]),
        espnowService.getMasterPumps(controllerId).catch(() => [] as MasterPump[]),
        espnowService.getPrograms(controllerId).catch(() => [] as ScheduleProgram[]),
        espnowService.getEvents(controllerId, 50).catch(() => [] as EspNowEvent[]),
        espnowService.getCommandLog(controllerId, 50).catch(() => [] as FieldCommand[]),
        espnowService.getScheduleState(controllerId).catch(() => [] as NodeScheduleState[]),
        orchardService.getFarms(1, 100).catch(() => null),
      ]);
      const farmList = farmsRes && farmsRes.success ? (farmsRes.data?.farms ?? []) : [];
      setFarms(farmList.map((f: { id: number; name: string }) => ({ id: f.id, name: f.name })));
      const ctrl = ctrls.find(c => c.controller_id === controllerId) ?? null;
      setController(ctrl);
      setNameValue(ctrl?.controller_name ?? controllerId ?? '');
      setRouters(rts);
      setMasterPumps(pumps);
      setPrograms(progs);
      setEvents(evts);
      setCommands(cmds);
      setScheduleState(sched);
    } catch {
      setError('Failed to load controller data');
    } finally {
      setLoading(false);
    }
  }, [controllerId]);

  useAppRefresh(useCallback(() => { load(); }, [load]));

  useEffect(() => {
    load();
    const interval = setInterval(() => {
      if (tab === 'routers' || tab === 'events') {
        espnowService.getRouters(controllerId!).then(setRouters).catch(() => {});
        espnowService.getEvents(controllerId!, 50).then(setEvents).catch(() => {});
        espnowService.getCommandLog(controllerId!, 50).then(setCommands).catch(() => {});
      }
      // Nodes only self-report every 5 min, but they also post immediately on
      // their first time sync — that is the moment `runnable` flips true across
      // a whole node, so poll often enough to redraw without a 5 min lag.
      if (tab === 'routers' || tab === 'schedules') {
        espnowService.getScheduleState(controllerId!).then(setScheduleState).catch(() => {});
      }
    }, 15_000);
    return () => clearInterval(interval);
  }, [load, tab, controllerId]);

  // Where a site's timers physically live decides which feed can answer "is
  // this valve part of a program, and is it due now".
  //
  // Nothing is ever pushed to an XBee site's nodes — the laptop bridge holds
  // the clock — so `schedule-state` comes back empty for every relay on every
  // node, and the relay tiles below showed no countdown, no "armed" and no sign
  // that a valve the schedule opens in three minutes is scheduled at all. The
  // same silence covered a valve whose window was open RIGHT NOW: the tile read
  // a bare "OFF", off a bitmask that on this site is only refreshed when
  // somebody runs Check State.
  //
  // The bridge's timeline is the other half of the pair the irrigation landing
  // page already learnt to read, and `buildLiveMapStatus` is the same
  // derivation the live map times its zones from — so the two cannot disagree.
  const bridgeTimed = controller?.transport === 'xbee';
  useEffect(() => {
    if (!controllerId || !bridgeTimed || (tab !== 'routers' && tab !== 'map')) return;
    let cancelled = false;
    const pull = () => espnowService.getLiveMap(controllerId)
      .then(data => { if (!cancelled) setLiveMap({ data, at: Date.now() }); })
      .catch(() => {});
    pull();
    const id = setInterval(pull, 20_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [controllerId, bridgeTimed, tab]);

  // The SITE's offset, not the viewer's browser timezone — see siteOffsetSec.
  const bridgeOffsetSec = siteOffsetSec(orgTzOrNull, controller?.timezone_offset);

  async function removeRouter(mac: string, label: string) {
    // Typed confirmation is overkill; a named one is not. The address is what the
    // operator sees on the card, and removing the wrong node means deleting its
    // relay names and zone assignments as well.
    if (!confirm(
      `Remove ${label} (${mac}) from this controller?

`
      + 'Its relay names and zone assignments go with it. Use this when a node has '
      + 'been replaced or taken out of service - the replacement has a different '
      + 'address and will appear on its own.'
    )) return;
    try {
      await espnowService.deleteRouter(controllerId!, mac);
      load();
    } catch (err) {
      // The 409 case is the useful one: it names the programs still watering this
      // node, which is exactly what has to be repointed first.
      alert(errorMessage(err, 'Could not remove this node'));
    }
  }

  async function saveName() {
    if (!controller) return;
    setSavingName(true);
    try {
      const updated = await espnowService.updateController(controller.controller_id, {
        controller_name: nameValue,
      });
      setController(prev => prev ? { ...prev, controller_name: updated.controller_name } : prev);
      setEditingName(false);
    } catch {
      setError('Failed to update name');
    } finally {
      setSavingName(false);
    }
  }

  async function handleRouterMapPlace(routerMac: string, lat: number, lng: number) {
    if (!controller) return;
    try {
      const updated = await espnowService.updateRouterLocation(routerMac, controller.controller_id, lat, lng);
      setRouters(prev => prev.map(r => r.router_mac === routerMac ? { ...r, lat: updated.lat, lng: updated.lng } : r));
    } catch {
      setError('Failed to save router location');
    }
  }

  async function handleUpdateRelayLabels(
    routerMac: string,
    labels: Record<string, string>,
    relayCount?: number | null,
    disabledRelays?: number[],
  ) {
    if (!controller) return;
    try {
      const updated = await espnowService.updateRouterLabels(
        routerMac, controller.controller_id, labels, relayCount, disabledRelays,
      );
      setRouters(prev => prev.map(r => r.router_mac === routerMac
        ? {
            ...r,
            relay_labels: updated.relay_labels,
            relay_count: updated.relay_count,
            disabled_relays: updated.disabled_relays ?? [],
          }
        : r));
    } catch {
      setError('Failed to save labels');
    }
  }

  async function handleUpdateRouterName(routerMac: string, name: string) {
    if (!controller) return;
    try {
      const updated = await espnowService.updateRouterName(routerMac, controller.controller_id, name);
      setRouters(prev => prev.map(r => r.router_mac === routerMac ? { ...r, name: updated.name } : r));

      // On an XBee site the name is not just a label we keep — it is the
      // radio's Node Identifier. Writing it here too (NI + WR) means the name
      // survives a re-discovery and shows up in any other tool that talks to
      // the mesh. Without this the server and the hardware disagree the moment
      // someone runs Find Nodes, and the discovered NI overwrites the label.
      if (controller.transport === 'xbee') {
        await espnowService.runDiagnostic(controller.controller_id, {
          action: 'SET_NODE_NAME',
          router_mac: routerMac,
          name,
        });
      }
    } catch {
      setError(`Failed to rename ${nodeNoun(controller.transport).oneLower}`);
    }
  }

  async function handleAddMasterPump(routerMac: string, relayIndex: number, label: string) {
    if (!controller) return;
    try {
      const pump = await espnowService.addMasterPump(controller.controller_id, {
        router_mac: routerMac,
        relay_index: relayIndex,
        label: label || null,
      });
      setMasterPumps(prev => [...prev, pump]);
    } catch {
      setError('Failed to add master pump');
    }
  }

  async function handleUpdateMasterPump(pumpId: string, data: { router_mac?: string; relay_index?: number; label?: string | null }) {
    try {
      const updated = await espnowService.updateMasterPump(pumpId, data);
      setMasterPumps(prev => prev.map(p => p.id === pumpId ? updated : p));
    } catch {
      setError('Failed to update master pump');
    }
  }

  async function handleDeleteMasterPump(pumpId: string) {
    try {
      await espnowService.deleteMasterPump(pumpId);
      setMasterPumps(prev => prev.filter(p => p.id !== pumpId));
    } catch {
      setError('Failed to delete master pump');
    }
  }

  async function requestStatusRefresh() {
    if (!controllerId || statusRefreshing) return;
    setStatusRefreshing(true);
    try {
      // Snapshot current relay_state_updated_at per router so we can detect fresh data
      const baseline: Record<string, string | null> = {};
      routers.forEach(r => { baseline[r.router_mac] = r.relay_state_updated_at; });

      await espnowService.sendCommand(controllerId, { type: 'GET_STATUS' });

      // Poll relay-states until any router has a newer timestamp (max 30s)
      const deadline = Date.now() + 30_000;
      while (Date.now() < deadline) {
        await new Promise(res => setTimeout(res, 2000));
        const fresh = await espnowService.getRelayStates(controllerId);
        const updated = fresh.some(r => r.relay_state_updated_at !== baseline[r.router_mac]);
        if (updated) {
          setRouters(prev => prev.map(r => {
            const s = fresh.find(f => f.router_mac === r.router_mac);
            return s ? { ...r, relay_states: s.relay_states, relay_state_updated_at: s.relay_state_updated_at } : r;
          }));
          break;
        }
      }
    } catch {
      setError('Failed to request status refresh');
    } finally {
      setStatusRefreshing(false);
    }
  }

  /**
   * Broadcast a TIMESYNC. A node with `clock_ok: false` has a dead scheduler —
   * its stored programs are correct but will never fire — so this is the single
   * repair action for the most common silent failure.
   */
  async function sendTimeSync() {
    if (!controllerId || timeSyncing) return;
    setTimeSyncing(true);
    setError(null);
    setSuccess(null);
    try {
      await espnowService.sendCommand(controllerId, { type: 'TIMESYNC' });
      setSuccess('Time sync queued. Nodes pick it up within ~10 s and re-report their schedules.');
      // Nodes post a fresh schedule-state immediately after their first sync;
      // give the mesh a moment, then pull it so `runnable` redraws.
      setTimeout(() => {
        espnowService.getScheduleState(controllerId).then(setScheduleState).catch(() => {});
      }, 12_000);
    } catch {
      setError('Failed to queue time sync');
    } finally {
      setTimeSyncing(false);
    }
  }

  /**
   * Tap a relay, then find out whether anything actually happened.
   *
   * Three things are asked in turn, because they are three different questions:
   *
   *   1. Did the server take the command?  — the POST
   *   2. Did the SITE carry it out?        — the command row reaching CONFIRMED
   *   3. Does the NODE report the output in that state? — a reading taken after
   *
   * Only (3) is evidence that a valve is open. The tile used to answer (1) and
   * paint itself as though it had answered (3), so a command the bridge later
   * refused outright — "failed after 5 attempts: node returned an error status"
   * is a real answer from a real site — still read as a working valve.
   *
   * (3) has to be ASKED for. An XBee node reports its outputs only when
   * something requests them, so waiting for a spontaneous report is waiting for
   * a message nobody is going to send.
   */
  async function handleToggleRelay(routerMac: string, relayIndex: number, currentlyOn: boolean) {
    if (!controllerId || !controller) return;
    const key = `${routerMac}:${relayIndex}`;
    if (relayCommandBusy(relayCommands.get(key))) return;

    const router = routers.find(r => r.router_mac === routerMac);
    const label = router?.relay_labels?.[String(relayIndex)] || `Relay ${relayIndex}`;
    const noun = nodeNoun(controller.transport).oneLower;
    const want = !currentlyOn;
    const verb = want ? 'turn on' : 'turn off';

    // No confirmation either way: a tap is the whole gesture, and the worst a
    // mistaken one can do is run the relay for MANUAL_RUN_MAX_SEC.

    const run = (relayRun.current.get(key) ?? 0) + 1;
    relayRun.current.set(key, run);
    /**
     * Writes a phase, unless a newer tap on this relay has taken over.
     *
     * `asked` defaults to what this tap asked for, but the trailing read after
     * a manual run times out is asking the opposite question — and a verdict
     * filed under the wrong intent paints a confirmed-OFF tile green.
     */
    const mark = (
      phase: RelayCommand['phase'],
      detail: string | null = null,
      asked: boolean = want,
    ) => {
      if (relayRun.current.get(key) !== run) return false;
      setRelayCommands(prev => new Map(prev).set(key, { want: asked, phase, detail, at: Date.now() }));
      return true;
    };
    const current = () => relayRun.current.get(key) === run;

    mark('sending');
    setError(null);

    let cmdId: string;
    try {
      const res = await espnowService.sendCommand(controllerId, want
        ? { type: 'SET_RELAY', router_mac: routerMac, relay: relayIndex, duration_sec: MANUAL_RUN_MAX_SEC }
        : { type: 'CLEAR_RELAY', router_mac: routerMac, relay: relayIndex });
      cmdId = res.cmd_id;
    } catch {
      if (mark('failed', 'The command could not be queued')) setError(`Failed to ${verb} ${label}`);
      return;
    }

    // ── The site's acknowledgement ────────────────────────────────────────────
    const ackBy = Date.now() + RELAY_ACK_WAIT_MS;
    let acked = false;
    while (Date.now() < ackBy && current()) {
      await sleep(RELAY_POLL_MS);
      const log = await espnowService.getCommandLog(controllerId, 20).catch(() => null);
      const mine = log?.find(c => c.cmd_id === cmdId);
      if (!mine) continue;
      if (mine.status === 'ERROR') {
        // The site's own words. They name the node and the channel, which is
        // more than any sentence this page could compose.
        if (mark('failed', mine.detail)) {
          setError(mine.detail || `The site refused to ${verb} ${label}`);
        }
        return;
      }
      if (mine.status === 'CONFIRMED' || mine.status === 'OK') { acked = true; break; }
    }
    if (!current()) return;
    if (!acked) {
      mark('unconfirmed', `The site has not acknowledged the command to ${verb} ${label}`);
      return;
    }
    mark('acked');

    // ── The node's own reading ────────────────────────────────────────────────
    if (!(await confirmRelay(routerMac, relayIndex, want, label, noun, mark, current))) return;

    // A manual run stops itself after MANUAL_RUN_MAX_SEC. Nothing reports that,
    // so a tile confirmed green would sit there claiming an open valve long
    // after the water stopped. Read it back once more when the run is due to
    // have ended, and let the answer stand.
    if (want) {
      await sleep(MANUAL_RUN_MAX_SEC * 1000);
      if (!current()) return;
      await confirmRelay(routerMac, relayIndex, false, label, noun, mark, current, true);
    }
  }

  /**
   * Wait for the node's own reading of the output.
   *
   * Nothing is asked for here. Both ends of the link sample the pins after a
   * relay changes and post what they find — the XBee bridge through
   * `_read_back_states` a couple of seconds after the boundary, an ESP-NOW node
   * on its own change report — so this only has to watch
   * `relay_state_updated_at` move. Issuing a READ_STATES from the browser as
   * well put a second frame on the mesh to learn what was already on its way.
   *
   * Returns false when nothing came back, or when the answer contradicts what
   * was asked for — either way the caller has nothing left to follow.
   *
   * `trailing` is the read taken once a manual run should have stopped itself.
   * Nothing was commanded there, so a relay still reporting on is a valve left
   * open rather than a command that missed, and it is worded as one.
   */
  async function confirmRelay(
    routerMac: string,
    relayIndex: number,
    want: boolean,
    label: string,
    noun: string,
    mark: (phase: RelayCommand['phase'], detail?: string | null, asked?: boolean) => boolean,
    current: () => boolean,
    trailing = false,
  ): Promise<boolean> {
    if (!controllerId || !controller || !current()) return false;
    mark('confirming', null, want);

    // Taken now rather than from the render closure: the 15 s poll may have
    // moved it while the command was in flight, and a baseline that is already
    // stale would read the poll's own refresh as this command's answer.
    const before = (await espnowService.getRelayStates(controllerId).catch(() => null))
      ?.find(r => r.router_mac === routerMac)?.relay_state_updated_at ?? null;

    const readBy = Date.now() + RELAY_READ_WAIT_MS;
    while (Date.now() < readBy && current()) {
      await sleep(RELAY_POLL_MS);
      const fresh = await espnowService.getRelayStates(controllerId).catch(() => null);
      const row = fresh?.find(f => f.router_mac === routerMac);
      if (!row || row.relay_state_updated_at === before) continue;

      setRouters(prev => prev.map(r => r.router_mac === routerMac
        ? { ...r, relay_states: row.relay_states, relay_state_updated_at: row.relay_state_updated_at }
        : r));

      const actual = getRelayState(row.relay_states, relayIndex);
      if (actual === want) { mark('confirmed', null, want); return true; }
      const said = trailing
        ? `${label} should have stopped itself after ${MANUAL_RUN_MAX_LABEL} and the ${noun} still reports it on`
        : `${label} was commanded ${want ? 'on' : 'off'} and the ${noun} reports it ${actual ? 'on' : 'off'}`;
      if (mark('mismatch', said, want)) setError(said);
      return false;
    }

    if (current()) {
      mark('unconfirmed', `No output reading came back from the ${noun} for ${label}`, want);
    }
    return false;
  }

  async function handleOtaUpdate(target: 'master' | 'router', routerMac?: string) {
    if (!controllerId) return;
    const key = target === 'master' ? 'master' : routerMac!;
    const label = target === 'master'
      ? 'master firmware'
      : `router ${routerMac?.slice(-6) ?? ''}`;
    if (!window.confirm(
      `Queue OTA firmware update for ${label}?\n\n` +
      `The device will download the latest firmware on its next command poll ` +
      `and reboot. This is typically a last-resort action.`
    )) return;

    setOtaSending(key);
    setError(null);
    setSuccess(null);
    try {
      await espnowService.queueOtaUpdate(controllerId, target, routerMac);
      setSuccess(
        target === 'master'
          ? 'OTA update queued. Master will download & reboot on its next poll (within 10s).'
          : `OTA update queued for ${routerMac}. Master will push creds + URL over ESP-NOW.`
      );
    } catch {
      setError(`Failed to queue OTA update for ${label}`);
    } finally {
      setOtaSending(null);
    }
  }

  async function handleFarmChange(farmId: number | null) {
    if (!controller) return;
    if (!canIrrigationAdmin) {
      setError('An Irrigation Admin license seat is required to change farm assignment.');
      return;
    }
    setSavingFarm(true);
    try {
      const updated = await espnowService.updateControllerFarm(controller.controller_id, farmId);
      setController(prev => prev ? { ...prev, farm_id: updated.farm_id } : prev);
    } catch {
      setError('Failed to update farm assignment');
    } finally {
      setSavingFarm(false);
    }
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6 flex justify-center items-center h-64 text-gray-400">
        <RefreshCw className="w-6 h-6 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  if (!controller) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        {error && (
          <div className="flex items-center gap-2 p-3 m-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
          </div>
        )}
        <div className="p-6 text-center text-gray-400">
          Controller not found or not assigned to your organisation.
        </div>
      </div>
    );
  }

  // Device-reported schedule snapshots, keyed by the MAC they belong to. Plain
  // computation rather than useMemo so it stays below the early-return guards.
  const schedByMac: Record<string, NodeScheduleState> = {};
  scheduleState.forEach(s => { schedByMac[s.router_mac] = s; });

  // The same snapshot for a bridge-timed site, keyed `mac:relay`. Folded into
  // the LOCAL day: the timeline's seconds are UTC but the programs in it were
  // selected by today's LOCAL weekday, and comparing the two raw reads a +10
  // site's whole morning as already past. See localDaySec.
  const bridgeValves: Map<string, ValveStatus> | null = liveMap
    ? buildLiveMapStatus({
        liveData: liveMap.data,
        routers,
        transport: controller?.transport ?? null,
        nowSec: localDaySec(
          liveMap.data.now_sec + Math.floor((Date.now() - liveMap.at) / 1000),
          bridgeOffsetSec
        ),
        offsetSec: bridgeOffsetSec,
        nowMs: Date.now(),
      }).byKey
    : null;

  // How many outputs are reported on right now, for the stop confirmation to
  // put a number on what is about to close. Counted off the bitmask rather than
  // off the schedule: what a program says should be running is not what is
  // running, and this is the figure somebody is about to act on. usableRelays
  // keeps a relay nobody wired out of the count.
  const openRelayCount = routers.reduce((total, r) => {
    const mask = Number(r.relay_states ?? 0);
    return total + usableRelays(r, controller?.transport)
      .filter(idx => (mask & (1 << idx)) !== 0).length;
  }, 0);

  // Fleet health, straight off what the hardware says — not off the programs
  // the server believes it pushed.
  const nodesWithoutClock = scheduleState.filter(s => !s.clock_ok);
  const deadSchedules = scheduleState.flatMap(s =>
    s.relays
      .filter(r => r.enabled && !r.runnable)
      .map(r => ({ node: s, relay: r }))
  );

  function relayDisplayName(mac: string, relayIndex: number): string {
    const r = routers.find(x => x.router_mac === mac);
    return r?.relay_labels?.[String(relayIndex)] || `Relay ${relayIndex}`;
  }

  function nodeDisplayName(mac: string): string {
    const r = routers.find(x => x.router_mac === mac);
    return r?.name || mac.slice(-6);
  }

  return (
    <>
      <PageHeader
        title={controller.controller_name || controller.controller_id}
        subtitle={[
          controller.controller_id,
          controller.online ? 'Online' : 'Offline',
          // An online laptop with no radio is the case this call-out exists
          // for: without it the header says "Online" while every node reads
          // offline, and the two together describe a farm-wide node failure
          // that is not happening.
          controller.online && controller.coordinator_connected === false
            ? 'no coordinator' : null,
          controller.device_type || null,
          controller.firmware_version != null ? `fw v${controller.firmware_version}` : null,
        ].filter(Boolean).join(' · ')}
        icon={controller.online ? <Wifi className="w-5 h-5 text-green-500" /> : <WifiOff className="w-5 h-5 text-gray-400" />}
        actionButtons={
          // Offered even when the controller reads offline. "Offline" means the
          // server has not heard from it lately, which is not the same as the
          // site having stopped watering — an XBee bridge whose uplink dropped
          // is still running its own timers perfectly. The command queues and is
          // collected when it comes back, and the hold applies immediately
          // either way.
          <StopWateringButton
            controllerName={controller.controller_name || controller.controller_id}
            openValves={openRelayCount}
            busy={wateringStop.busy}
            disabled={Boolean(wateringStop.status?.paused)}
            onStop={(reason) => wateringStop.stop(reason)}
          />
        }
      />
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div>
          {/* First thing on the page, above even the error strip: a site that
              will not water until somebody says so explains every other empty
              answer below it. */}
          <WateringHoldBanner
            status={wateringStop.status}
            timezone={orgTimezone}
            canResume={canIrrigationAdmin}
            busy={wateringStop.busy}
            onResume={wateringStop.resume}
            className="mb-4"
          />
          {wateringStop.error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 mb-4">
              <AlertCircle className="w-4 h-4 flex-shrink-0" /> {wateringStop.error}
            </div>
          )}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 mb-4">
              <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
              <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">×</button>
            </div>
          )}
          {success && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700 mb-4">
              {success}
              <button onClick={() => setSuccess(null)} className="ml-auto text-green-400 hover:text-green-600">×</button>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-1 border-b border-gray-200 mb-5 overflow-x-auto">
            {([
              { id: 'routers', label: noun.many, icon: <Radio className="w-4 h-4" /> },
              { id: 'schedules', label: 'Schedules', icon: <Calendar className="w-4 h-4" /> },
              { id: 'map', label: 'Live Map', icon: <MapIcon className="w-4 h-4" /> },
              { id: 'sensors', label: 'Sensors', icon: <Activity className="w-4 h-4" /> },
              { id: 'events', label: 'Events', icon: <Terminal className="w-4 h-4" /> },
              { id: 'settings', label: 'Settings', icon: <Settings className="w-4 h-4" /> },
            ] as { id: Tab; label: string; icon: React.ReactNode }[]).map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex-shrink-0 flex items-center gap-1.5 px-3 sm:px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              >
                {t.icon} {t.label}
              </button>
            ))}
          </div>

          {/* Routers tab — live status only */}
          {tab === 'routers' && (
            <div className="space-y-4">
              {/* Fleet-level schedule health, from device-reported state (§3.7). */}
              {nodesWithoutClock.length > 0 && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-800">
                  <ClockAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-medium">
                      {nodesWithoutClock.length === 1 ? '1 node has' : `${nodesWithoutClock.length} nodes have`} no clock.
                    </span>{' '}
                    Nothing on {nodesWithoutClock.length === 1 ? 'it' : 'them'} will water until a time sync
                    arrives, however good the schedules look —{' '}
                    {nodesWithoutClock.map(s => nodeDisplayName(s.router_mac)).join(', ')}.
                    <button
                      onClick={() => sendTimeSync()}
                      disabled={!controller.online || timeSyncing}
                      className="ml-2 underline font-medium disabled:opacity-50 disabled:no-underline"
                    >
                      {timeSyncing ? 'Sending…' : 'Send time sync'}
                    </button>
                  </div>
                </div>
              )}
              {deadSchedules.length > 0 && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
                  <CircleSlash className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-medium">
                      {deadSchedules.length} stored {deadSchedules.length === 1 ? 'schedule' : 'schedules'} will not run.
                    </span>
                    <ul className="mt-1 space-y-0.5 text-xs">
                      {deadSchedules.slice(0, 6).map(({ node, relay }) => (
                        <li key={`${node.router_mac}-${relay.relay}`}>
                          {nodeDisplayName(node.router_mac)} · {relayDisplayName(node.router_mac, relay.relay)}
                          {relay.reason ? ` — ${relay.reason}` : ''}
                        </li>
                      ))}
                      {deadSchedules.length > 6 && (
                        <li className="italic">…and {deadSchedules.length - 6} more.</li>
                      )}
                    </ul>
                  </div>
                </div>
              )}

              {/* Relay status refresh */}
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={sendTimeSync}
                  disabled={timeSyncing || !controller.online}
                  title={!controller.online ? 'Controller is offline' : 'Broadcast a time sync to every node'}
                >
                  <Clock className={`w-4 h-4 sm:mr-1.5 ${timeSyncing ? 'animate-spin' : ''}`} />
                  <span className="hidden sm:inline">
                    {timeSyncing ? 'Syncing…' : 'Time Sync'}
                  </span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={requestStatusRefresh}
                  disabled={statusRefreshing || !controller.online}
                  title={!controller.online ? 'Controller is offline' : 'Request relay states from all routers'}
                >
                  <RefreshCw className={`w-4 h-4 sm:mr-1.5 ${statusRefreshing ? 'animate-spin' : ''}`} />
                  <span className="hidden sm:inline">
                    {statusRefreshing ? 'Refreshing…' : 'Refresh Status'}
                  </span>
                </Button>
              </div>

              {/* Mesh diagnostics — XBee only. ESP-NOW masters do not implement
                  these commands and the server rejects them for that transport,
                  so showing the panel would just offer buttons that always fail. */}
              {controller?.transport === 'xbee' && (
                <div className="mb-4">
                  <MeshDiagnostics
                    controllerId={controller.controller_id}
                    transport={controller.transport}
                    routers={routers}
                    online={controller.online}
                    coordinatorConnected={controller.coordinator_connected}
                    coordinatorLastOk={controller.coordinator_last_ok}
                    coordinatorError={controller.coordinator_error}
                    onRefresh={load}
                    commands={commands}
                    onShowLog={() => setTab('events')}
                  />
                </div>
              )}

              {/* The same list for a site with no mesh diagnostics to sit
                  inside. An ESP-NOW master holds no XBee panel, and it is the
                  transport whose schedules ARE dispatched as commands — so
                  without this the one site type whose nightly dispatch is
                  visible in field_commands was the one with nowhere to see it. */}
              {controller && controller.transport !== 'xbee' && commands.length > 0 && (
                <Card className="mb-4">
                  <CardContent className="pt-4">
                    <RecentActivity
                      results={[]}
                      commands={commands}
                      routers={routers}
                      transport={controller.transport}
                      onRefresh={load}
                      onShowLog={() => setTab('events')}
                    />
                  </CardContent>
                </Card>
              )}

              {/* Getting a node INTO the list, as distinct from interrogating the
                  ones already in it. Separate panel because discovery is a
                  broadcast and can always miss: this is the address typed off the
                  radio's label, and the radios the bridge has overheard but is not
                  allowed to adopt on its own. XBee only, for the same reason the
                  diagnostics panel is. */}
              {controller?.transport === 'xbee' && (
                <div className="mb-4">
                  <NodeEnrolment
                    controllerId={controller.controller_id}
                    transport={controller.transport}
                    routers={routers}
                    online={controller.online}
                    onRefresh={load}
                  />
                </div>
              )}

              {/* Router cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {routers.map(router => (
                  <RouterDetailCard
                    controllerId={controller.controller_id}
                    transport={controller?.transport}
                    key={router.router_mac}
                    router={router}
                    schedule={schedByMac[router.router_mac] ?? null}
                    bridgeValves={bridgeValves}
                    timezoneOffsetSec={controller.timezone_offset ?? 0}
                    masterPumps={masterPumps}
                    onSaveLabels={(labels, relayCount, disabledRelays) => handleUpdateRelayLabels(router.router_mac, labels, relayCount, disabledRelays)}
                    onRename={name => handleUpdateRouterName(router.router_mac, name)}
                    isSuperAdmin={isSuperAdmin}
                    canRemove={isAdmin}
                    onRemove={() => removeRouter(
                      router.router_mac, router.name || router.router_mac.slice(-6))}
                    onUpdateFirmware={() => handleOtaUpdate('router', router.router_mac)}
                    otaBusy={otaSending === router.router_mac}
                    controllerOnline={!!controller.online}
                    stateUnknown={controller.coordinator_connected === false}
                    onToggleRelay={(relayIndex, currentlyOn) => handleToggleRelay(router.router_mac, relayIndex, currentlyOn)}
                    relayCommands={relayCommands}
                    onTestFinished={() => {
                      espnowService.getRouters(controllerId!).then(setRouters).catch(() => {});
                    }}
                  />
                ))}
                {routers.length === 0 && (
                  <p className="text-sm text-gray-400 col-span-full italic">No routers have announced themselves yet. Power on the ESP32 mesh.</p>
                )}
              </div>
            </div>
          )}

          {/* Settings tab */}
          {tab === 'settings' && (
            <div className="space-y-6">
              {/* Renders nothing unless this is an XBee laptop site. */}
              <BridgeUpdatePanel controller={controller} onRefreshController={load} />
              <SettingsTab
                controller={controller}
                farms={farms}
                savingFarm={savingFarm}
                onFarmChange={handleFarmChange}
                editingName={editingName}
                nameValue={nameValue}
                savingName={savingName}
                onStartRename={() => setEditingName(true)}
                onCancelRename={() => setEditingName(false)}
                onNameChange={setNameValue}
                onSaveName={saveName}
                isSuperAdmin={isSuperAdmin}
                otaSending={otaSending}
                onOtaMaster={() => handleOtaUpdate('master')}
                masterPumps={masterPumps}
                routers={routers}
                onAddPump={handleAddMasterPump}
                onUpdatePump={handleUpdateMasterPump}
                onDeletePump={handleDeleteMasterPump}
              />
            </div>
          )}

          {/* Schedules tab */}
          {tab === 'schedules' && (
            <div className="min-h-96">
              <RainSkipPanel
                controller={controller}
                programs={programs}
                routers={routers}
                orgTimezone={orgTimezone}
              />
              <EspNowScheduleBuilder
                controller={controller}
                initialProgramId={focusProgramId}
                programs={programs}
                routers={routers}
                masterPumps={masterPumps}
                orgTimezone={orgTimezone}
                onProgramsChange={setPrograms}
              />
            </div>
          )}

          {/* Map tab */}
          {tab === 'map' && (
            <EspNowLiveMap
              controller={controller}
              routers={routers}
              orgTimezone={orgTimezone}
              onPlaceRouter={handleRouterMapPlace}
              height={520}
            />
          )}

          {/* Sensors tab */}
          {tab === 'sensors' && controllerId && (
            <SensorsTab
              controllerId={controllerId}
              orgId={controller.organisation_id}
              routers={routers}
            />
          )}

          {/* Events tab */}
          {tab === 'events' && (
            <div className="space-y-8">
              {/* Commands first. When something did not happen, "what did we ask
                  and what did it say" is almost always the question, and the
                  answer used to be in the database and nowhere else. */}
              <CommandsTable commands={commands} routers={routers} />
              <EventsTable events={events} routers={routers} transport={controller?.transport} />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Sensors Tab ─────────────────────────────────────────────────────────────

const CHANNEL_TYPE_ICONS: Record<string, React.ReactNode> = {
  temperature: <Thermometer className="w-4 h-4 text-orange-500" />,
  humidity: <Droplets className="w-4 h-4 text-sky-500" />,
  water_level: <Waves className="w-4 h-4 text-blue-500" />,
  tank_level: <Droplets className="w-4 h-4 text-blue-600" />,
  digital_input: <ToggleLeft className="w-4 h-4 text-gray-500" />,
  generic_adc: <Activity className="w-4 h-4 text-violet-500" />,
  soil_tension: <Sprout className="w-4 h-4 text-green-600" />,
};

// How a channel's raw value becomes its reading (sensorDeviceService.calibrateValue).
type Calibration = 'none' | 'linear' | 'watermark';

// Where a Watermark's soil temperature comes from (HHNodeServer
// soilTemperatureService). Open-Meteo models four depths; the typed figure is
// what a channel set to it falls back on when the model cannot be read.
type SoilTempSource = 'open_meteo' | 'fixed';
const SOIL_TEMP_DEPTHS: Array<{ cm: string; label: string }> = [
  { cm: '0', label: 'Surface (0 cm)' },
  { cm: '6', label: '6 cm' },
  { cm: '18', label: '18 cm' },
  { cm: '54', label: '54 cm' },
];

function soilTempNote(raw: Record<string, unknown> | null | undefined): string | null {
  const c = Number(raw?.soil_temp_c);
  if (raw?.soil_temp_c == null || !Number.isFinite(c)) return null;
  switch (raw.soil_temp_source) {
    case 'open_meteo': return `at ${c.toFixed(1)} °C, Open-Meteo ${String(raw.soil_temp_depth_cm)} cm`;
    case 'fixed_fallback':
    case 'default_fallback': return `at ${c.toFixed(1)} °C, Open-Meteo unavailable`;
    default: return `at ${c.toFixed(1)} °C`;
  }
}

/**
 * A node in the mesh that can carry sensor channels: the master controller's own
 * on-board inputs, or any remote router.
 *
 * `routerMac` is the *addressing* key used by every sensor endpoint, and it is
 * deliberately null for the master — the master's own inputs are stored with
 * field_node_mac IS NULL, even though its espnow_routers row carries its MAC.
 */
interface SensorNode {
  key: string;
  routerMac: string | null;
  label: string;
  sublabel: string;
  online: boolean;
  /**
   * Reachability verdict, when the node has one. `undefined` for the rows this
   * screen invents (the master's own inputs, a configured device whose node has
   * gone from the registry) - those are not nodes with a radio to be reached.
   */
  state?: 'online' | 'offline' | 'unknown' | null;
  isLocal: boolean;
  /** null when the node hasn't reported capabilities (pre-capability firmware). */
  diCount: number | null;
  adcCount: number | null;
}

function buildSensorNodes(
  routers: EspNowRouter[],
  devices: ControllerSensorData[]
): SensorNode[] {
  const nodes: SensorNode[] = routers.map(r => {
    const isLocal = r.is_local === true;
    return {
      key: r.router_mac,
      routerMac: isLocal ? null : r.router_mac,
      label: isLocal
        ? 'Master (on-board inputs)'
        : (r.name || r.router_mac.slice(-8)),
      sublabel: r.device_type || r.router_mac,
      online: r.online,
      state: r.state ?? null,
      isLocal,
      diCount: r.di_count ?? null,
      adcCount: r.adc_count ?? null,
    };
  });

  // The master's own inputs are always configurable, so make sure there is a
  // node for them even when the master hasn't published a local row — a master
  // with no relays didn't publish one at all on older firmware, and its existing
  // sensor config would otherwise be unreachable from this screen.
  if (!nodes.some(n => n.isLocal)) {
    nodes.unshift({
      key: '__master__',
      routerMac: null,
      label: 'Master (on-board inputs)',
      sublabel: 'Controller',
      online: true,
      isLocal: true,
      diCount: null,
      adcCount: null,
    });
  }

  // Surface any configured device whose node is missing from the router list
  // (router removed from the registry, re-flashed, or temporarily dropped out)
  // rather than silently hiding its channels and readings.
  for (const d of devices) {
    const mac = d.field_node_mac ?? null;
    if (!mac || nodes.some(n => n.routerMac === mac)) continue;
    nodes.push({
      key: mac,
      routerMac: mac,
      label: d.router_name || d.name || mac.slice(-8),
      sublabel: `${mac} · not currently reporting`,
      online: false,
      state: 'unknown',
      isLocal: false,
      diCount: null,
      adcCount: null,
    });
  }

  return nodes;
}

function SensorsTab({
  controllerId,
  orgId: _orgId,
  routers,
}: {
  controllerId: string;
  orgId: number | null;
  routers: EspNowRouter[];
}) {
  const sensorTz = useOrgTimezone();
  // Sensor-device setup and channel config are admin-only within irrigation.
  const { hasAccess: canIrrigationAdmin } = useModuleLicense('irrigation', 'admin');

  // null = the master's own inputs, which is also the safe default when the
  // router list hasn't loaded yet.
  const [selectedMac, setSelectedMac] = useState<string | null>(null);
  // Every sensor device on this controller, keyed by node. One request serves
  // both the node badges and the selected node's channels.
  const [devices, setDevices] = useState<ControllerSensorData[]>([]);
  const sensorNodes = useMemo(() => buildSensorNodes(routers, devices), [routers, devices]);
  const [notSetUp, setNotSetUp] = useState(false);
  const [sensorTypes, setSensorTypes] = useState<SensorType[]>([]);
  const [loading, setLoading] = useState(true);
  const [settingUp, setSettingUp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingChannel, setEditingChannel] = useState<{ kind: 'analog' | 'digital'; index: number } | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [addForm, setAddForm] = useState({
    kind: 'analog' as 'analog' | 'digital',
    index: 0,
    type_key: 'generic_adc',
    name: '',
    calibration: 'none' as Calibration,
    soil_temp_c: '24',
    soil_temp_source: 'open_meteo' as SoilTempSource,
    soil_temp_depth_cm: '18',
    adc_min: '0',
    adc_max: '4095',
    value_min: '0',
    value_max: '100',
    smoothing: '1',
    is_tank: false,
    tank_diameter_mm: '',
    tank_height_mm: '',
  });
  const [saving, setSaving] = useState(false);

  // Apply sensible defaults when the sensor type changes (only for new channels).
  function applyTypeDefaults(typeKey: string) {
    setAddForm(f => {
      // Don't clobber the user's values when editing an existing channel
      if (editingChannel) return { ...f, type_key: typeKey };
      switch (typeKey) {
        case 'temperature':
          return { ...f, type_key: typeKey, kind: 'analog', calibration: 'linear',
            adc_min: '0', adc_max: '4095', value_min: '-40', value_max: '125', is_tank: false };
        case 'humidity':
          return { ...f, type_key: typeKey, kind: 'analog', calibration: 'linear',
            adc_min: '0', adc_max: '4095', value_min: '0', value_max: '100', is_tank: false };
        case 'water_level':
          return { ...f, type_key: typeKey, kind: 'analog', calibration: 'linear',
            adc_min: '0', adc_max: '4095', value_min: '0', value_max: '2000' };
        case 'soil_tension':
          // Watermark firmware reports ohms, so only the Watermark curve applies.
          return { ...f, type_key: typeKey, kind: 'analog', calibration: 'watermark', is_tank: false };
        case 'digital_input':
          return { ...f, type_key: typeKey, kind: 'digital', calibration: 'none', is_tank: false };
        default: // generic_adc
          return { ...f, type_key: typeKey, kind: 'analog', calibration: 'none', is_tank: false };
      }
    });
  }

  const loadSensor = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, types] = await Promise.all([
        espnowService.listSensorsForController(controllerId),
        sensorDevicesService.listTypes(),
      ]);
      setSensorTypes(types);
      setDevices(list);
    } catch {
      setError('Failed to load sensor data');
    } finally {
      setLoading(false);
    }
  }, [controllerId]);

  useEffect(() => { loadSensor(); }, [loadSensor]);

  // The device for the node currently selected. Deriving it rather than storing
  // it keeps the selector and the channel list from drifting apart.
  const sensorData = useMemo(
    () => devices.find(d => (d.field_node_mac ?? null) === selectedMac) ?? null,
    [devices, selectedMac]
  );
  useEffect(() => { setNotSetUp(!loading && !sensorData); }, [loading, sensorData]);

  // Switching nodes must not carry a half-finished channel form across.
  useEffect(() => {
    setShowAddForm(false);
    setEditingChannel(null);
  }, [selectedMac]);

  const selectedNode = sensorNodes.find(n => n.routerMac === selectedMac) ?? null;
  const selectedNodeLabel = selectedNode?.label
    ?? (selectedMac ? selectedMac : 'Master (on-board inputs)');

  async function handleSetup() {
    if (!canIrrigationAdmin) {
      setError('An Irrigation Admin license seat is required to set up the sensor device.');
      return;
    }
    setSettingUp(true);
    setError(null);
    try {
      await espnowService.setupSensor(controllerId, selectedMac);
      setSuccess(`Sensor device created for ${selectedNodeLabel}. Now add channels to configure inputs.`);
      await loadSensor();
    } catch {
      setError('Failed to set up sensor device');
    } finally {
      setSettingUp(false);
    }
  }

  async function handleAddChannel() {
    if (!sensorData) return;
    if (!canIrrigationAdmin) {
      setError('An Irrigation Admin license seat is required to manage sensor channels.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const config: Record<string, unknown> = {};
      if (addForm.calibration === 'linear') {
        config.calibration = 'linear';
        config.adc_min = Number(addForm.adc_min);
        config.adc_max = Number(addForm.adc_max);
        config.value_min = Number(addForm.value_min);
        config.value_max = Number(addForm.value_max);
      } else if (addForm.calibration === 'watermark') {
        config.calibration = 'watermark';
        config.soil_temp_source = addForm.soil_temp_source;
        if (addForm.soil_temp_source === 'open_meteo') config.soil_temp_depth_cm = Number(addForm.soil_temp_depth_cm);
        const t = Number(addForm.soil_temp_c);
        if (addForm.soil_temp_c.trim() !== '' && Number.isFinite(t)) config.soil_temp_c = t;
      } else {
        config.calibration = 'none';
      }
      const smoothing = Number(addForm.smoothing) || 1;
      if (smoothing > 1) config.smoothing = smoothing;
      if (addForm.type_key === 'water_level' && addForm.is_tank) {
        const d = Number(addForm.tank_diameter_mm);
        const h = Number(addForm.tank_height_mm);
        if (!(d > 0) || !(h > 0)) {
          setError('Tank diameter and height must be positive numbers');
          setSaving(false);
          return;
        }
        config.tank = {
          shape: 'cylinder_vertical',
          diameter_mm: d,
          height_mm: h,
        };
      }
      await espnowService.upsertSensorChannel(
        controllerId,
        addForm.kind,
        addForm.index,
        {
          type_key: addForm.type_key,
          name: addForm.name || null,
          config,
        },
        selectedMac
      );
      setShowAddForm(false);
      setEditingChannel(null);
      if (!editingChannel) {
        setAddForm(f => ({ ...f, name: '', index: f.index + 1 }));
      }
      setSuccess(editingChannel ? 'Channel updated' : 'Channel added');
      await loadSensor();
    } catch {
      setError(editingChannel ? 'Failed to update channel' : 'Failed to add channel');
    } finally {
      setSaving(false);
    }
  }

  function handleEditChannel(ch: SensorChannel) {
    const cfg = ch.config as Record<string, unknown>;
    const isLinear = cfg.calibration === 'linear';
    const isWatermark = cfg.calibration === 'watermark';
    const tank = (cfg.tank as { diameter_mm?: number; height_mm?: number } | undefined) || undefined;
    setAddForm({
      kind: ch.channel_kind as 'analog' | 'digital',
      index: ch.channel_index,
      type_key: ch.type_key,
      name: ch.name || '',
      calibration: isLinear ? 'linear' : isWatermark ? 'watermark' : 'none',
      soil_temp_c: String(cfg.soil_temp_c ?? '24'),
      // A channel saved before this has no source: it was always the typed figure.
      soil_temp_source: cfg.soil_temp_source === 'open_meteo' ? 'open_meteo' : 'fixed',
      soil_temp_depth_cm: String(cfg.soil_temp_depth_cm ?? '18'),
      adc_min: String(cfg.adc_min ?? '0'),
      adc_max: String(cfg.adc_max ?? '4095'),
      value_min: String(cfg.value_min ?? '0'),
      value_max: String(cfg.value_max ?? '100'),
      smoothing: String(cfg.smoothing ?? '1'),
      is_tank: !!tank,
      tank_diameter_mm: tank?.diameter_mm != null ? String(tank.diameter_mm) : '',
      tank_height_mm: tank?.height_mm != null ? String(tank.height_mm) : '',
    });
    setEditingChannel({ kind: ch.channel_kind as 'analog' | 'digital', index: ch.channel_index });
    setShowAdvanced(isLinear || isWatermark || Number(cfg.smoothing ?? 1) > 1);
    setShowAddForm(true);
  }

  async function handleDeleteChannel(kind: 'analog' | 'digital', index: number) {
    if (!canIrrigationAdmin) {
      setError('An Irrigation Admin license seat is required to remove sensor channels.');
      return;
    }
    if (!confirm(`Remove ${kind} channel ${index} on ${selectedNodeLabel}?`)) return;
    try {
      await espnowService.deleteSensorChannel(controllerId, kind, index, selectedMac);
      await loadSensor();
    } catch {
      setError('Failed to delete channel');
    }
  }

  function getLatestForChannel(kind: string, index: number) {
    if (!sensorData?.latestReadings) return null;
    return sensorData.latestReadings.find(
      r => r.channel_index === index && r.channel_kind === kind
    ) ?? null;
  }

  if (loading) {
    return (
      <div className="text-sm text-gray-400 flex items-center gap-2">
        <RefreshCw className="w-4 h-4 animate-spin" /> Loading sensor data…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-400">×</button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
          {success}
          <button onClick={() => setSuccess(null)} className="ml-auto text-green-400">×</button>
        </div>
      )}

      {/* Node selector — which device in the mesh these channels belong to.
          Each node has its own sensor device, channels and reading history. */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Radio className="w-4 h-4 text-blue-500" />
            Read sensors from
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {sensorNodes.length === 0 ? (
            <p className="text-sm text-gray-500">
              No nodes reported yet. Once the master and its routers check in they
              will appear here and you can configure each one's inputs.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {sensorNodes.map(node => {
                const dev = devices.find(d => (d.field_node_mac ?? null) === node.routerMac);
                const channelCount = dev?.channels?.length ?? 0;
                const selected = node.routerMac === selectedMac;
                // Capability counts are null on nodes running firmware from
                // before they were advertised — that is "unknown", not "none",
                // so we only call out a node as having no inputs when it said so.
                const knownNoInputs =
                  node.diCount !== null && node.adcCount !== null &&
                  node.diCount === 0 && node.adcCount === 0;
                return (
                  <button
                    key={node.key}
                    type="button"
                    onClick={() => setSelectedMac(node.routerMac)}
                    className={`text-left rounded-xl border px-3 py-2.5 transition-colors ${
                      selected
                        ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                        : 'border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          node.online ? 'bg-green-500'
                            // Amber, not red: see the router card. Nothing has
                            // asked this node, so nothing is being claimed.
                            : node.state === 'offline' ? 'bg-red-500' : 'bg-amber-400'
                        }`}
                      />
                      <span className="text-sm font-medium text-gray-800 truncate">
                        {node.label}
                      </span>
                      {node.isLocal && (
                        <span className="text-[10px] uppercase tracking-wide text-amber-600 bg-amber-50 border border-amber-200 rounded px-1">
                          master
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-gray-400 mt-0.5 truncate">
                      {node.sublabel}
                    </div>
                    <div className="text-[11px] mt-1 flex items-center gap-2 flex-wrap">
                      {channelCount > 0 ? (
                        <span className="text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5">
                          {channelCount} channel{channelCount === 1 ? '' : 's'}
                        </span>
                      ) : (
                        <span className="text-gray-400">not set up</span>
                      )}
                      {node.adcCount !== null && node.adcCount > 0 && (
                        <span className="text-gray-500">{node.adcCount} ADC</span>
                      )}
                      {node.diCount !== null && node.diCount > 0 && (
                        <span className="text-gray-500">{node.diCount} DI</span>
                      )}
                      {knownNoInputs && (
                        <span className="text-gray-400 italic">no sensor inputs</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {notSetUp ? (
        <Card>
          <CardContent className="py-8 text-center">
            <Activity className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500 mb-4">
              <span className="font-medium text-gray-700">{selectedNodeLabel}</span> has
              not been set up as a sensor device yet. Set it up to configure its
              ADC/digital input channels and start recording readings.
            </p>
            <Button onClick={handleSetup} disabled={settingUp}>
              <Plus className="w-4 h-4 mr-1" /> {settingUp ? 'Setting up…' : 'Set Up as Sensor'}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Channel list */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex flex-wrap items-center justify-between gap-2">
                <span className="flex flex-wrap items-center gap-2 min-w-0">
                  <Activity className="w-4 h-4 text-violet-500" />
                  Channels ({sensorData?.channels?.length || 0})
                  <span className="text-xs font-normal text-gray-400">
                    on {selectedNodeLabel}
                  </span>
                </span>
                <div className="flex items-center gap-2">
                  {sensorData && (
                    <Link to={`/irrigation/sensors/${encodeURIComponent(sensorData.device_id)}`}>
                      <Button variant="outline" size="sm">View Readings</Button>
                    </Link>
                  )}
                  <button
                    onClick={() => setShowAddForm(f => !f)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add Channel
                  </button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {showAddForm && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 space-y-3">
                  {editingChannel && (
                    <div className="text-sm font-medium text-blue-800">
                      Editing {editingChannel.kind} channel {editingChannel.index}
                    </div>
                  )}

                  {/* Primary fields — most users only need these */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label className="block">
                      <span className="text-xs text-gray-700">Sensor Type</span>
                      <select
                        value={addForm.type_key}
                        onChange={e => applyTypeDefaults(e.target.value)}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      >
                        {sensorTypes.map(t => (
                          <option key={t.type_key} value={t.type_key}>
                            {t.display_name}{t.unit ? ` (${t.unit})` : ''}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className="text-xs text-gray-700">Name</span>
                      <input
                        type="text"
                        value={addForm.name}
                        onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))}
                        placeholder="e.g. Pool Depth"
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs text-gray-700">Channel</span>
                      <select
                        value={addForm.index}
                        onChange={e => setAddForm(f => ({ ...f, index: Number(e.target.value) }))}
                        disabled={!!editingChannel}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500"
                      >
                        {/* Only the channels the node reports having; firmware too old to
                            report its counts (null) still gets the full 0-15. */}
                        {Array.from({
                          length: (addForm.kind === 'digital' ? selectedNode?.diCount : selectedNode?.adcCount) ?? 16,
                        }, (_, i) => (
                          <option key={i} value={i}>{addForm.kind === 'digital' ? 'DI' : 'ADC'} {i}</option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className="text-xs text-gray-700">Input Kind</span>
                      <select
                        value={addForm.kind}
                        onChange={e => setAddForm(f => ({ ...f, kind: e.target.value as 'analog' | 'digital' }))}
                        disabled={!!editingChannel}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500"
                      >
                        <option value="analog">Analog (ADC)</option>
                        <option value="digital">Digital</option>
                      </select>
                    </label>
                  </div>

                  {/* Tank-specific block — shown when type=water_level */}
                  {addForm.type_key === 'water_level' && (
                    <div className="border-t border-blue-200 pt-3 space-y-2">
                      <label className="flex items-center gap-2 text-sm font-medium text-blue-800">
                        <input
                          type="checkbox"
                          checked={addForm.is_tank}
                          onChange={e => setAddForm(f => ({ ...f, is_tank: e.target.checked }))}
                          className="rounded"
                        />
                        Water tank — calculate volume &amp; usage trends
                      </label>
                      {addForm.is_tank && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pl-6">
                          <label className="block">
                            <span className="text-xs text-gray-700">Tank diameter (mm)</span>
                            <input type="number" min="1" step="1"
                              value={addForm.tank_diameter_mm}
                              onChange={e => setAddForm(f => ({ ...f, tank_diameter_mm: e.target.value }))}
                              placeholder="e.g. 1200"
                              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                          </label>
                          <label className="block">
                            <span className="text-xs text-gray-700">Tank height (mm)</span>
                            <input type="number" min="1" step="1"
                              value={addForm.tank_height_mm}
                              onChange={e => setAddForm(f => ({ ...f, tank_height_mm: e.target.value }))}
                              placeholder="e.g. 2000"
                              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                          </label>
                          <p className="sm:col-span-2 text-xs text-gray-500">
                            Vertical cylindrical tank. Sensor reports fill depth in mm.
                            Capacity ≈ {(() => {
                              const d = Number(addForm.tank_diameter_mm);
                              const h = Number(addForm.tank_height_mm);
                              if (d > 0 && h > 0) return (Math.PI * (d / 2) ** 2 * h / 1_000_000).toFixed(0) + ' L';
                              return '—';
                            })()}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Advanced / calibration — collapsed by default */}
                  <div className="border-t border-blue-200 pt-3">
                    <button
                      type="button"
                      onClick={() => setShowAdvanced(v => !v)}
                      className="text-xs font-medium text-blue-700 hover:text-blue-900"
                    >
                      {showAdvanced ? '▾' : '▸'} Advanced: calibration &amp; smoothing
                    </button>
                    {showAdvanced && (
                      <div className="mt-3 space-y-3">
                        <label className="block">
                          <span className="text-xs text-gray-700">Calibration</span>
                          <select
                            value={addForm.calibration}
                            onChange={e => setAddForm(f => ({ ...f, calibration: e.target.value as Calibration }))}
                            className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                          >
                            <option value="none">None (raw value)</option>
                            <option value="linear">Linear (scale ADC to units)</option>
                            <option value="watermark">Watermark soil sensor (ohms → kPa)</option>
                          </select>
                        </label>
                        {addForm.calibration === 'watermark' && (
                          <div className="space-y-2">
                            <label className="block">
                              <span className="text-xs text-gray-700">Soil temperature</span>
                              <select
                                value={addForm.soil_temp_source}
                                onChange={e => setAddForm(f => ({ ...f, soil_temp_source: e.target.value as SoilTempSource }))}
                                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                              >
                                <option value="open_meteo">From Open-Meteo (modelled for the farm)</option>
                                <option value="fixed">A figure I type in</option>
                              </select>
                            </label>
                            {addForm.soil_temp_source === 'open_meteo' && (
                              <label className="block">
                                <span className="text-xs text-gray-700">Depth</span>
                                <div className="flex flex-wrap items-center gap-2 mt-1">
                                  <select
                                    value={addForm.soil_temp_depth_cm}
                                    onChange={e => setAddForm(f => ({ ...f, soil_temp_depth_cm: e.target.value }))}
                                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                  >
                                    {SOIL_TEMP_DEPTHS.map(d => <option key={d.cm} value={d.cm}>{d.label}</option>)}
                                  </select>
                                  <span className="text-xs text-gray-500 min-w-0 flex-1">
                                    Choose the depth nearest the sensor. Modelled at the farm&apos;s centre, so the sensor must be on a farm.
                                  </span>
                                </div>
                              </label>
                            )}
                            <label className="block">
                              <span className="text-xs text-gray-700">
                                {addForm.soil_temp_source === 'open_meteo' ? 'If Open-Meteo cannot be read (°C)' : 'Soil temperature (°C)'}
                              </span>
                              <div className="flex flex-wrap items-center gap-2 mt-1">
                                <input type="number" value={addForm.soil_temp_c}
                                  onChange={e => setAddForm(f => ({ ...f, soil_temp_c: e.target.value }))}
                                  className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                                <span className="text-xs text-gray-500 min-w-0 flex-1">
                                  {addForm.soil_temp_source === 'open_meteo'
                                    ? 'Used only when no modelled figure is available.'
                                    : 'At sensor depth. Readings shift ~1-2% per °C; update it seasonally.'}
                                </span>
                              </div>
                            </label>
                          </div>
                        )}
                        {addForm.calibration === 'linear' && (
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <label className="block">
                              <span className="text-xs text-gray-700">ADC Min</span>
                              <input type="number" value={addForm.adc_min} onChange={e => setAddForm(f => ({ ...f, adc_min: e.target.value }))}
                                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                            </label>
                            <label className="block">
                              <span className="text-xs text-gray-700">ADC Max</span>
                              <input type="number" value={addForm.adc_max} onChange={e => setAddForm(f => ({ ...f, adc_max: e.target.value }))}
                                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                            </label>
                            <label className="block">
                              <span className="text-xs text-gray-700">Value Min</span>
                              <input type="number" value={addForm.value_min} onChange={e => setAddForm(f => ({ ...f, value_min: e.target.value }))}
                                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                            </label>
                            <label className="block">
                              <span className="text-xs text-gray-700">Value Max</span>
                              <input type="number" value={addForm.value_max} onChange={e => setAddForm(f => ({ ...f, value_max: e.target.value }))}
                                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                            </label>
                          </div>
                        )}
                        {addForm.kind === 'analog' && (
                          <label className="block">
                            <span className="text-xs text-gray-700">Smoothing window</span>
                            <div className="flex items-center gap-2 mt-1">
                              <input type="number" min="1" max="20" value={addForm.smoothing}
                                onChange={e => setAddForm(f => ({ ...f, smoothing: e.target.value }))}
                                className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                              <span className="text-xs text-gray-500">
                                {Number(addForm.smoothing) <= 1
                                  ? 'No smoothing — raw values used directly'
                                  : `Averages the last ${addForm.smoothing} readings`}
                              </span>
                            </div>
                          </label>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 pt-1">
                    <Button onClick={handleAddChannel} disabled={saving} size="sm">
                      {saving
                        ? (editingChannel ? 'Saving…' : 'Adding…')
                        : (editingChannel ? 'Save Changes' : 'Add Channel')}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setShowAddForm(false); setEditingChannel(null); setShowAdvanced(false); }}>Cancel</Button>
                  </div>
                </div>
              )}

              {(!sensorData?.channels || sensorData.channels.length === 0) ? (
                <p className="text-sm text-gray-400 italic">
                  No channels configured. Add a channel to start recording sensor data.
                </p>
              ) : (
                <div className="space-y-2">
                  {sensorData.channels.map(ch => {
                    const latest = getLatestForChannel(ch.channel_kind, ch.channel_index);
                    const cfg = ch.config as Record<string, unknown>;
                    const smoothingVal = Number(cfg.smoothing) || 1;
                    const calLabel = (cfg.calibration === 'linear'
                      ? `Linear ${cfg.adc_min ?? 0}-${cfg.adc_max ?? 4095} → ${cfg.value_min ?? 0}-${cfg.value_max ?? 100}`
                      : cfg.calibration === 'watermark'
                        ? (cfg.soil_temp_source === 'open_meteo'
                          ? `Watermark @ Open-Meteo ${String(cfg.soil_temp_depth_cm ?? 18)} cm`
                          : `Watermark @ ${String(cfg.soil_temp_c ?? 24)} °C`)
                        : 'Raw')
                      + (smoothingVal > 1 ? ` · avg ${smoothingVal}` : '');

                    return (
                      <div key={`${ch.channel_kind}-${ch.channel_index}`}
                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200 flex-wrap"
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1 basis-48">
                          {CHANNEL_TYPE_ICONS[ch.type_key] || <Activity className="w-4 h-4 text-gray-400" />}
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-800 break-words">
                              {ch.name || `${ch.channel_kind === 'digital' ? 'DI' : 'ADC'} ${ch.channel_index}`}
                            </div>
                            <div className="text-xs text-gray-500 break-words">
                              {ch.type_display_name}{ch.type_unit ? ` (${ch.type_unit})` : ''} · {ch.channel_kind} ch{ch.channel_index} · {calLabel}
                            </div>
                          </div>
                        </div>
                        <div className="ml-auto flex items-center gap-3">
                          {latest ? (
                            <div className="text-right">
                              <div className="text-lg font-bold text-gray-800">
                                {latest.value !== null ? Number(latest.value).toFixed(1) : '—'}
                                {ch.type_unit ? <span className="text-sm font-normal text-gray-500 ml-1">{ch.type_unit}</span> : null}
                              </div>
                              <div className="text-xs text-gray-400">
                                {formatTimeInTz(latest.reading_time, sensorTz)}
                              </div>
                              {cfg.calibration === 'watermark' && soilTempNote(latest.raw) && (
                                <div className="text-xs text-gray-400">{soilTempNote(latest.raw)}</div>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">No readings</span>
                          )}
                          <button
                            onClick={() => handleEditChannel(ch)}
                            title="Edit channel"
                            className="p-1.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleDeleteChannel(ch.channel_kind as 'analog' | 'digital', ch.channel_index)}
                            title="Remove channel"
                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

// ─── Settings Tab ─────────────────────────────────────────────────────────────

function SettingsTab({
  controller,
  farms,
  savingFarm,
  onFarmChange,
  editingName,
  nameValue,
  savingName,
  onStartRename,
  onCancelRename,
  onNameChange,
  onSaveName,
  isSuperAdmin,
  otaSending,
  onOtaMaster,
  masterPumps,
  routers,
  onAddPump,
  onUpdatePump,
  onDeletePump,
}: {
  controller: EspNowController;
  farms: Array<{ id: number; name: string }>;
  savingFarm: boolean;
  onFarmChange: (farmId: number | null) => void;
  editingName: boolean;
  nameValue: string;
  savingName: boolean;
  onStartRename: () => void;
  onCancelRename: () => void;
  onNameChange: (v: string) => void;
  onSaveName: () => void;
  isSuperAdmin: boolean;
  otaSending: string | null;
  onOtaMaster: () => void;
  masterPumps: MasterPump[];
  routers: EspNowRouter[];
  onAddPump: (routerMac: string, relayIndex: number, label: string) => void;
  onUpdatePump: (pumpId: string, data: { router_mac?: string; relay_index?: number; label?: string | null }) => void;
  onDeletePump: (pumpId: string) => void;
}) {
  return (
    <div className="space-y-4">
      {/* Controller name */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Pencil className="w-4 h-4 text-blue-500" /> Controller Name
          </CardTitle>
        </CardHeader>
        <CardContent>
          {editingName ? (
            <div className="flex items-center gap-2 flex-wrap">
              <input
                autoFocus
                value={nameValue}
                onChange={e => onNameChange(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') onSaveName(); if (e.key === 'Escape') onCancelRename(); }}
                className="flex-1 min-w-0 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Button size="sm" onClick={onSaveName} disabled={savingName}>
                {savingName ? 'Saving…' : 'Save'}
              </Button>
              <Button variant="outline" size="sm" onClick={onCancelRename}>Cancel</Button>
            </div>
          ) : (
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-800">
                  {controller.controller_name || <span className="italic text-gray-400">(no name set)</span>}
                </div>
                <div className="text-xs text-gray-400 font-mono mt-0.5">{controller.controller_id}</div>
              </div>
              <Button variant="outline" size="sm" onClick={onStartRename}>Rename</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Farm assignment */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Building2 className="w-4 h-4 text-green-600" /> Farm Assignment
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 flex-wrap">
            <select
              value={controller.farm_id ?? ''}
              onChange={e => onFarmChange(e.target.value ? Number(e.target.value) : null)}
              disabled={savingFarm || farms.length === 0}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 disabled:bg-gray-50"
            >
              <option value="">No farm assigned</option>
              {farms.map(f => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
            {savingFarm && <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />}
          </div>
          <p className="text-xs text-gray-400 mt-2">
            {farms.length === 0
              ? 'No farms defined. Create one under Farms first.'
              : 'Linking to a farm groups this controller with others on the same property.'}
          </p>
        </CardContent>
      </Card>

      {/* Hardware — what the master reported about itself at registration */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Cpu className="w-4 h-4 text-slate-500" /> Hardware
          </CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3 text-sm">
            <HardwareFact label="Device type" value={controller.device_type ?? '—'} />
            <HardwareFact label="MAC" value={controller.mac ?? '—'} mono />
            <HardwareFact label="Last IP" value={controller.ip ?? '—'} mono />
            <HardwareFact
              label="WiFi channel"
              value={controller.wifi_channel != null ? String(controller.wifi_channel) : '—'}
            />
            <HardwareFact
              label="ESP-NOW channel"
              value={controller.espnow_channel != null ? String(controller.espnow_channel) : '—'}
              // A mismatch means the mesh cannot form; the firmware only logs it to serial.
              warn={
                controller.espnow_channel != null &&
                controller.wifi_channel != null &&
                controller.espnow_channel !== controller.wifi_channel
              }
              warnText="Does not match the WiFi channel — the mesh will not form."
            />
            <HardwareFact
              label="Clock offset"
              value={
                controller.timezone_offset === 0
                  ? 'UTC'
                  : `UTC${controller.timezone_offset > 0 ? '+' : '−'}${formatDuration(Math.abs(controller.timezone_offset))}`
              }
            />
          </dl>
          <div className="mt-4 pt-3 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-500 mb-2">On-board I/O</p>
            <div className="flex flex-wrap gap-1.5">
              <CapabilityChip label="Relays" present={controller.has_local_relays} />
              <CapabilityChip label="Digital inputs" present={controller.has_local_inputs} />
              <CapabilityChip label="Analog inputs" present={controller.has_local_analog} />
            </div>
            {controller.has_local_relays && (
              <p className="text-xs text-gray-400 mt-2">
                The master's own relays report alongside the routers — look for{' '}
                {controller.mac ? <span className="font-mono">{controller.mac}</span> : 'its own MAC'} on the Routers tab.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Master pumps */}
      <MasterPumpConfig
        masterPumps={masterPumps}
        routers={routers}
        onAdd={onAddPump}
        onUpdate={onUpdatePump}
        onDelete={onDeletePump}
      />

      {/* OTA (super admin only).
          Never on an XBee site: "master firmware" is an ESP32 image queued for
          a device to fetch on its next command poll, and an XBee site has no
          such device - its coordinator is a laptop running the Python bridge,
          which updates itself through BridgeUpdatePanel at the top of this tab.
          Shown here the button was a dead control that looked like the right
          one, sitting directly below the one that actually works. */}
      {isSuperAdmin && controller.transport !== 'xbee' && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Upload className="w-4 h-4 text-gray-500" /> Firmware
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-gray-700">
                  Master firmware{' '}
                  {controller.firmware_version != null && (
                    <span className="text-xs text-gray-400">· v{controller.firmware_version}</span>
                  )}
                </div>
                <div className="text-xs text-gray-400 mt-0.5">
                  Router firmware updates are available from each router card on the Routers tab.
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={onOtaMaster}
                disabled={otaSending !== null || !controller.online}
                title={!controller.online ? 'Controller is offline' : 'Queue an OTA firmware update for the master'}
              >
                <Upload className={`w-4 h-4 sm:mr-1 ${otaSending === 'master' ? 'animate-pulse' : ''}`} />
                <span className="hidden sm:inline">
                  {otaSending === 'master' ? 'Queuing…' : 'Update Master'}
                </span>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function HardwareFact({
  label,
  value,
  mono,
  warn,
  warnText,
}: {
  label: string;
  value: string;
  mono?: boolean;
  warn?: boolean;
  warnText?: string;
}) {
  return (
    <div>
      <dt className="text-xs text-gray-400">{label}</dt>
      <dd
        className={`mt-0.5 ${mono ? 'font-mono text-xs' : 'text-sm'} ${
          warn ? 'text-amber-600 font-medium' : 'text-gray-800'
        }`}
        title={warn ? warnText : undefined}
      >
        {value}
        {warn && <AlertCircle className="w-3.5 h-3.5 inline ml-1 -mt-0.5" />}
      </dd>
    </div>
  );
}

function CapabilityChip({ label, present }: { label: string; present: boolean | null }) {
  // null means the master has not reported this yet — not the same as "absent".
  if (present == null) {
    return (
      <span className="px-2 py-0.5 rounded-full text-xs bg-gray-50 border border-gray-200 text-gray-400">
        {label} · unknown
      </span>
    );
  }
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
        present
          ? 'bg-green-50 border border-green-200 text-green-700'
          : 'bg-gray-50 border border-gray-200 text-gray-400'
      }`}
    >
      {label} {present ? '✓' : '—'}
    </span>
  );
}

// ─── Master Pump Config ───────────────────────────────────────────────────────

function MasterPumpConfig({
  masterPumps,
  routers,
  onAdd,
  onUpdate,
  onDelete,
}: {
  masterPumps: MasterPump[];
  routers: EspNowRouter[];
  onAdd: (routerMac: string, relayIndex: number, label: string) => void;
  onUpdate: (pumpId: string, data: { router_mac?: string; relay_index?: number; label?: string | null }) => void;
  onDelete: (pumpId: string) => void;
}) {
  const [showAdd, setShowAdd] = useState(false);
  const [addRouterMac, setAddRouterMac] = useState(routers[0]?.router_mac ?? '');
  const [addRelayIndex, setAddRelayIndex] = useState(0);
  const [addLabel, setAddLabel] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');

  function handleAdd() {
    if (!addRouterMac) return;
    onAdd(addRouterMac, addRelayIndex, addLabel.trim());
    setShowAdd(false);
    setAddLabel('');
    setAddRelayIndex(0);
  }

  function startEdit(pump: MasterPump) {
    setEditingId(pump.id);
    setEditLabel(pump.label ?? '');
  }

  function saveEdit(pump: MasterPump) {
    onUpdate(pump.id, { label: editLabel.trim() || null });
    setEditingId(null);
  }

  const selectedAddRouter = routers.find(r => r.router_mac === addRouterMac);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-500" /> Master Pumps / Valves
          </span>
          <button
            onClick={() => { setShowAdd(f => !f); setAddRouterMac(routers[0]?.router_mac ?? ''); }}
            className="flex items-center gap-1 px-2.5 py-1 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-lg"
          >
            <Plus className="w-3.5 h-3.5" /> Add Pump
          </button>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        <p className="text-xs text-gray-400">
          Master pumps run automatically when any relay assigned to them is active.
          Each zone relay can choose which pump supplies it.
        </p>

        {/* Add form */}
        {showAdd && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-3">
            <p className="text-xs font-medium text-amber-800">New master pump / valve</p>
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Router</label>
                <select
                  value={addRouterMac}
                  onChange={e => {
                    setAddRouterMac(e.target.value);
                    // Its first relay IN USE, not its relay 0 — on a board
                    // wired from channel 2 up, relay 0 is not an option the
                    // list below offers, and a select whose value is not among
                    // its options shows blank.
                    const next = routers.find(r => r.router_mac === e.target.value);
                    setAddRelayIndex(usableRelays(next)[0] ?? 0);
                  }}
                  className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                >
                  {routers.map(r => (
                    <option key={r.router_mac} value={r.router_mac}>
                      {r.name || r.router_mac.slice(-8)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Relay</label>
                <select
                  value={addRelayIndex}
                  onChange={e => setAddRelayIndex(parseInt(e.target.value))}
                  className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                >
                  {usableRelays(selectedAddRouter).map(i => {
                    const lbl = selectedAddRouter?.relay_labels?.[String(i)];
                    return <option key={i} value={i}>{lbl || `Relay ${i}`}</option>;
                  })}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Label</label>
                <input
                  type="text"
                  maxLength={64}
                  placeholder="e.g. Main Pump"
                  value={addLabel}
                  onChange={e => setAddLabel(e.target.value)}
                  className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 w-36"
                />
              </div>
              <div className="flex gap-2">
                <button onClick={handleAdd} disabled={!addRouterMac} className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs rounded-lg font-medium disabled:opacity-50">
                  Add
                </button>
                <button onClick={() => setShowAdd(false)} className="px-3 py-1.5 border border-gray-300 text-xs rounded-lg hover:bg-gray-50">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pump list */}
        {masterPumps.length === 0 ? (
          <p className="text-sm text-gray-400 italic">No master pumps configured.</p>
        ) : (
          masterPumps.map(pump => {
            const pumpRouter = routers.find(r => r.router_mac === pump.router_mac);
            const routerLabel = pumpRouter?.name || pump.router_mac.slice(-8);
            const relayLabel = pumpRouter?.relay_labels?.[String(pump.relay_index)] || `Relay ${pump.relay_index}`;
            return (
              <div key={pump.id} className="flex items-center gap-3 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                <Zap className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  {editingId === pump.id ? (
                    <div className="flex items-center gap-2">
                      <input
                        autoFocus
                        maxLength={64}
                        value={editLabel}
                        onChange={e => setEditLabel(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') saveEdit(pump); if (e.key === 'Escape') setEditingId(null); }}
                        className="border border-gray-300 rounded px-2 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-amber-400 w-36"
                      />
                      <button onClick={() => saveEdit(pump)} className="text-xs text-amber-600 hover:underline">Save</button>
                      <button onClick={() => setEditingId(null)} className="text-xs text-gray-400 hover:underline">Cancel</button>
                    </div>
                  ) : (
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-medium text-amber-800">
                        {pump.label || `${routerLabel} / ${relayLabel}`}
                      </span>
                      <span className="text-xs text-gray-400">{routerLabel} · {relayLabel}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => startEdit(pump)} className="p-1 text-gray-400 hover:text-amber-600 rounded" title="Edit label">
                    <Settings className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => onDelete(pump.id)} className="p-1 text-gray-400 hover:text-red-500 rounded" title="Remove pump">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}

// ─── Router Detail Card ───────────────────────────────────────────────────────

function RouterDetailCard({
  controllerId,
  transport,
  router,
  schedule,
  bridgeValves,
  timezoneOffsetSec,
  masterPumps,
  onSaveLabels,
  onRename,
  isSuperAdmin,
  canRemove,
  onRemove,
  onUpdateFirmware,
  otaBusy,
  controllerOnline,
  stateUnknown,
  onToggleRelay,
  relayCommands,
  onTestFinished,
}: {
  controllerId: string;
  transport?: string | null;
  router: EspNowRouter;
  schedule: NodeScheduleState | null;
  /**
   * What the BRIDGE's timeline says each of this node's valves is doing, keyed
   * `mac:relay`. Null on a site whose nodes hold their own timers — there the
   * node's own `schedule` snapshot is the better answer and this is not read.
   */
  bridgeValves: Map<string, ValveStatus> | null;
  timezoneOffsetSec: number;
  masterPumps: MasterPump[];
  onSaveLabels: (labels: Record<string, string>, relayCount?: number | null, disabledRelays?: number[]) => void;
  onRename: (name: string) => void;
  isSuperAdmin: boolean;
  /** Admin or super admin - the same gate the delete endpoint applies. */
  canRemove: boolean;
  onRemove: () => void;
  onUpdateFirmware: () => void;
  otaBusy: boolean;
  controllerOnline: boolean;
  /**
   * The bridge cannot currently reach the mesh, so `router.online` is the last
   * value it managed to read rather than the truth. Rendered as "unknown"
   * rather than offline: a red dot on every node is a specific, wrong claim —
   * that the nodes failed — when the actual fault is upstream of all of them.
   *
   * Note this is only the COORDINATOR-level unknown. A node can also be unknown
   * on its own account — nothing heard from it and nothing asked — which arrives
   * as router.state and is handled the same way, for the same reason.
   */
  stateUnknown: boolean;
  onToggleRelay: (relayIndex: number, currentlyOn: boolean) => void;
  /**
   * Manual taps still being followed, site-wide, keyed `mac:relay`. A tap is
   * three claims deep — queued, acknowledged by the site, reported by the node
   * — and the tile colours the last one that has actually arrived.
   */
  relayCommands: Map<string, RelayCommand>;
  /** A finished per-node test has new readings on the server; pull them in. */
  onTestFinished: () => void;
}) {
  const orgTz = useOrgTimezone();
  const noun = nodeNoun(transport);
  // Unverified, from either direction: the coordinator cannot be reached, or the
  // node itself has neither been heard from nor asked. Both mean "we do not
  // know", and neither is a fault to report.
  const unverified = stateUnknown || router.state === 'unknown';
  // Only ever true when something actually tried and failed.
  const failed = router.state ? router.state === 'offline' : (!router.online && !unverified);
  const [labels, setLabels] = useState<Record<string, string>>(router.relay_labels ?? {});
  const [editingLabels, setEditingLabels] = useState(false);
  const [countValue, setCountValue] = useState<number | null>(router.relay_count);
  // Relays the board has that are wired to nothing. Held as a Set while editing
  // so the checkboxes stay independent of the list's order.
  const [disabledValue, setDisabledValue] = useState<Set<number>>(
    () => new Set(router.disabled_relays ?? [])
  );
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(router.name ?? '');
  const pumpsOnThisRouter = masterPumps.filter(p => p.router_mac === router.router_mac);
  const isMasterRouter = pumpsOnThisRouter.length > 0;

  // Keep local state in sync with prop updates (e.g. after rename or auto-refresh)
  useEffect(() => {
    if (!editingName) setNameValue(router.name ?? '');
  }, [router.name, editingName]);

  useEffect(() => {
    if (!editingLabels) {
      setLabels(router.relay_labels ?? {});
      setCountValue(router.relay_count);
      setDisabledValue(new Set(router.disabled_relays ?? []));
    }
  }, [router.relay_labels, router.relay_count, router.disabled_relays, editingLabels]);

  // Only render relays the board physically has.
  //
  // A node's own schedule snapshot is the best answer when there is one, but an
  // XBee node never posts one - the bridge holds its timers - so that read as 8
  // forever, and a 4-relay board showed four controls that open nothing.
  // relayCountFor falls back on the operator-set count, then on the transport.
  const reportsOwnCount = (schedule?.relay_count ?? 0) > 0;
  const relayCount = schedule?.relay_count || relayCountFor(router);
  // While editing, the grid follows the box so rows appear and vanish as typed.
  const editRelayCount = editingLabels && !reportsOwnCount
    ? relayCountFor({ relay_count: countValue, transport: router.transport })
    : relayCount;
  const relayStates = Array.from({ length: relayCount }, (_, i) => getRelayState(router.relay_states, i));
  // Read once per render so every tile on the card ages its tap identically.
  // The page re-renders on its own 15 s poll and on every phase change, which
  // is often enough for a 90 s verdict to clear itself.
  const nowMs = Date.now();
  const activeCount = relayStates.filter(Boolean).length;

  // Relays to draw as controls. Out-of-use ones come off — they are the
  // invented-valve problem relay_count already solved, one level finer: a
  // 4-relay board wired on 0 and 3 has to keep saying 4, or channel 3 becomes
  // unreachable, so the two in the middle are subtracted instead.
  //
  // Except when one is ON. A relay marked out of use that still carries a
  // schedule row is still driven by the hardware, and hiding an energised valve
  // is a worse failure than showing a channel nobody wants.
  //
  // On a bridge-timed site the bitmask alone cannot make that judgement — it is
  // only refreshed when somebody asks — so a valve the bridge has a window open
  // on counts as energised too.
  const outOfUse = new Set(router.disabled_relays ?? []);
  const bridgeDriving = (i: number) => {
    const state = bridgeValves?.get(`${router.router_mac}:${i}`)?.state;
    return state === 'running' || state === 'due' || state === 'did-not-start';
  };
  const shownRelays = Array.from({ length: relayCount }, (_, i) => i)
    .filter(i => !outOfUse.has(i) || relayStates[i] || bridgeDriving(i));
  const hiddenRelayCount = relayCount - shownRelays.length;

  const schedByRelay: Record<number, RelayScheduleState> = {};
  schedule?.relays.forEach(r => { schedByRelay[r.relay] = r; });
  const armedCount = schedule?.relays.filter(r => r.runnable).length ?? 0;
  const skew = schedule ? clockSkewSec(schedule.secs_today, schedule.reported_at, timezoneOffsetSec) : null;
  const skewBad = skew !== null && Math.abs(skew) > 120;

  function saveLabels() {
    // A node that reports its own count owns the field - sending a value back
    // would be overwritten by its next announce anyway. Only offer the operator
    // the setting where the hardware has nothing to say (every XBee node).
    //
    // disabled_relays is always sent: no node can report which of its channels
    // somebody has wired up, so the operator's answer is the only answer, on
    // every transport.
    //
    // Clamped to the relays the board now has. Shrinking the count with relay 6
    // marked out of use would otherwise leave a hidden entry that reappears the
    // day somebody widens the board again.
    const kept = [...disabledValue].filter(i => i < editRelayCount).sort((a, b) => a - b);
    onSaveLabels(labels, reportsOwnCount ? undefined : countValue, kept);
    setEditingLabels(false);
  }

  function saveRouterName() {
    onRename(nameValue.trim());
    setEditingName(false);
  }

  // Deliberately NOT dimmed when the node did not answer. A faded card reads as
  // "nothing here works", which is the opposite of true: a node that has gone
  // quiet is precisely the one somebody is standing in front of with a
  // multimeter. The status dot and its tooltip make the claim; the card does
  // not need to also make the tools look broken.
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            {editingName ? (
              <div className="flex items-center gap-1.5">
                <input
                  autoFocus
                  maxLength={16}
                  value={nameValue}
                  onChange={e => setNameValue(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') saveRouterName(); if (e.key === 'Escape') setEditingName(false); }}
                  className="border border-gray-300 rounded px-2 py-0.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-32"
                />
                <button onClick={saveRouterName} className="text-xs text-blue-600 hover:underline">Save</button>
                <button onClick={() => setEditingName(false)} className="text-xs text-gray-400 hover:underline">Cancel</button>
              </div>
            ) : (
              <CardTitle className="flex items-center gap-2 text-base">
                <span
                  className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    unverified ? 'bg-amber-400'
                      : failed ? 'bg-red-500' : 'bg-green-500'
                  }`}
                  title={
                    stateUnknown
                      ? `State unknown — the coordinator is disconnected, so this is the last value read${router.last_seen ? ` (${formatDateTimeInTz(router.last_seen, orgTz)})` : ''}`
                      : unverified
                        // The honest version of what used to be a red dot: this
                        // node has not been asked, so nothing is being claimed.
                        // Watering it, or running Find nodes, settles it.
                        ? `Not verified — nothing heard from this ${noun.oneLower} recently and nothing has asked it. It has NOT failed; use it or run "Find ${noun.many}" to check`
                        : failed
                          ? `Did not answer — a command or check was sent to this ${noun.oneLower} and got no reply${router.last_seen ? `. Last heard ${formatDateTimeInTz(router.last_seen, orgTz)}` : ''}`
                          : 'Online'}
                />
                <button
                  onClick={() => { setNameValue(router.name ?? ''); setEditingName(true); }}
                  className="hover:text-blue-600 transition-colors text-left"
                  title="Rename router"
                >
                  {router.name || router.router_mac.slice(-6)}
                </button>
                {isMasterRouter && <span className="text-amber-500 text-sm" title="Contains master pump relay">⚡</span>}
              </CardTitle>
            )}
            <p className="text-xs text-gray-400 mt-0.5">
              {router.router_mac}
              {router.rssi_dbm !== null && ` · ${router.rssi_dbm} dBm`}
              {router.device_id !== null && ` · ID ${router.device_id}`}
              {router.device_type && ` · ${router.device_type}`}
              {router.firmware_version != null && ` · fw v${router.firmware_version}`}
              {router.transport && ` · ${router.transport.toUpperCase()}`}
              {router.transport === 'lora' && router.lora_provisioned === false && (
                <span className="text-amber-500" title="LoRa not provisioned"> · unprovisioned</span>
              )}
            </p>
          </div>
          <div className="text-right text-xs text-gray-400 flex-shrink-0 flex items-center gap-2">
            {activeCount > 0 && (
              <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                {activeCount} active
              </span>
            )}
            {isSuperAdmin && (
              <button
                onClick={onUpdateFirmware}
                disabled={otaBusy}
                /* An OTA is queued at the master, not handed to the node, so an
                   unreachable node is no reason to lock it: a node that dropped
                   off mid-update is one of the few this is actually for. */
                title={failed || unverified
                  ? `Queue an OTA firmware update — this ${noun.oneLower} is not answering, so it will apply whenever it comes back`
                  : 'Queue OTA firmware update for this router'}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Upload className={`w-3 h-3 ${otaBusy ? 'animate-pulse' : ''}`} />
                {otaBusy ? 'Queuing…' : 'Update'}
              </button>
            )}
            {/* Deliberately NOT disabled when the node is unreachable: the usual
                reason to remove one is that it is broken, so requiring it to
                answer first would lock the button exactly when it is wanted. */}
            {canRemove && (
              <button
                onClick={onRemove}
                title={`Remove this ${noun.oneLower} from the controller`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border border-red-200 text-red-600 hover:bg-red-50"
              >
                <Trash2 className="w-3 h-3" />
                Remove
              </button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        {/* A node with no clock has a dead scheduler — every stored program on
            it is inert. That outranks anything the individual tiles can say. */}
        {schedule && !schedule.clock_ok && (
          <div className="flex items-start gap-1.5 p-2 bg-red-50 border border-red-200 rounded-lg text-[11px] text-red-800">
            <ClockAlert className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
            <span>
              <span className="font-medium">No clock.</span> This node has had no time sync since boot,
              so its scheduler is a no-op — nothing here will water.
            </span>
          </div>
        )}
        {schedule && schedule.clock_ok && skewBad && (
          <div className="flex items-start gap-1.5 p-2 bg-amber-50 border border-amber-200 rounded-lg text-[11px] text-amber-800">
            <ClockAlert className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
            <span>
              <span className="font-medium">Clock is {formatDuration(Math.abs(skew!))} {skew! > 0 ? 'ahead' : 'behind'}.</span>{' '}
              Programs will start at the wrong time — node reads {formatSecsOfDay(schedule.secs_today!)}.
            </span>
          </div>
        )}

        {/* Relay tiles — click to toggle (test). Each carries the node's own
            answer to "will this water?" beneath the live on/off state. */}
        <div className="grid grid-cols-4 gap-1.5">
          {shownRelays.map(i => {
            // What the node last reported. A tap in flight is tracked beside it
            // rather than written over it — the two disagreeing is the single
            // most useful thing this tile can say.
            const on = relayStates[i];
            const cmd = relayCommands.get(`${router.router_mac}:${i}`) ?? null;
            const busy = relayCommandBusy(cmd);
            // What the NEXT tap should ask for. Not the bitmask: on a site whose
            // outputs are only read when asked, the bitmask can still be showing
            // the state from before the tap, and tapping "off" would re-send "on".
            const intended = relayIntendedState(cmd, on, nowMs);
            const isMasterRelay = pumpsOnThisRouter.some(p => p.relay_index === i);
            const label = labels[String(i)] || `Relay ${i}`;
            // Enabled whatever the NODE's state - unverified or failed alike.
            // "Did not answer the last command" is history, not a prediction:
            // the node may have been power-cycled, re-seated or driven out to
            // since, and the only way to find out is to send it something.
            // Disabling these told the operator a node had failed AND took away
            // the one control that would have shown otherwise, exactly when
            // they were trying to fix it. The red dot carries the claim.
            //
            // The CONTROLLER being offline is different: nothing leaves the
            // server, and a queued valve command firing unattended whenever the
            // bridge next reconnects is its own hazard. That stays disabled.
            //
            // A tap already in flight on THIS relay is blocked; one on a
            // neighbour is not. Following a command through to the node's own
            // reading takes a minute or more, and locking the whole card for it
            // would make the confirmation cost more than it is worth.
            const disabled = !controllerOnline || busy;
            const sched = schedByRelay[i];
            // A node that holds its own timers is its own best witness, so its
            // snapshot wins. The bridge's timeline answers for every valve it
            // does not cover — on an XBee site that is all of them.
            const bridge = sched ? null : bridgeValves?.get(`${router.router_mac}:${i}`) ?? null;
            // A window the bridge has opened is the same claim a tap makes and
            // gets the same amber, until the node's own reading turns it green
            // — or red, if the node says the output is shut.
            const tile = withScheduledTone(relayTile(cmd, on, nowMs), bridge?.state);

            // `runnable` is computed by the firmware — never re-derive it here.
            const wontRun = sched ? sched.enabled && !sched.runnable : false;
            // "armed" is the node's word and stays the node's word — nothing is
            // armed on an XBee node. A bridge-timed valve says what the bridge's
            // clock says about it instead.
            const bridgeNote = !bridge
              ? null
              : bridge.state === 'running' && bridge.remainingSec != null
              ? `${formatCountdown(Math.max(0, bridge.remainingSec))} left`
              : bridge.state === 'did-not-start'
              ? 'did not start'
              : bridge.state === 'due'
              ? 'due now'
              : bridge.state === 'skipped'
              ? 'skipped'
              : bridge.state === 'upcoming' && bridge.startsInSec != null
              ? `in ${formatCountdown(bridge.startsInSec)}`
              : null;
            const scheduleNote = !sched
              ? bridgeNote
              : wontRun
              ? "won't run"
              : sched.runnable && sched.next_run_in_sec != null
              ? `in ${formatCountdown(sched.next_run_in_sec)}`
              : sched.runnable
              ? 'armed'
              : null;

            const bridgeTitle = !bridge
              ? ''
              : bridge.state === 'running'
              ? `\nWatering now — ${formatDuration(Math.max(0, bridge.remainingSec ?? 0))} left of ${formatDuration(bridge.durationSec ?? 0)} (bridge clock)`
              : bridge.state === 'did-not-start'
              ? `\nThe schedule opened this valve ${formatDuration(bridge.elapsedSec ?? 0)} ago and the ${noun.oneLower} reports the output shut`
              : bridge.state === 'due'
              ? `\nScheduled to be watering now — ${formatDuration(Math.max(0, bridge.remainingSec ?? 0))} left.`
                + `\nThe ${noun.oneLower} has not reported its outputs since the window opened, so whether it is open is unknown.`
              : bridge.state === 'skipped'
              ? `\nThe sequence that would have opened this valve was skipped`
              : bridge.state === 'upcoming' && bridge.startsInSec != null
              ? `\nScheduled in ${formatCountdown(bridge.startsInSec)} for ${formatDuration(bridge.durationSec ?? 0)} (bridge clock)`
              : '';
            const scheduleTitle = !sched
              ? bridgeTitle
              : wontRun
              ? `\nStored but will not run${sched.reason ? ` — ${sched.reason}` : ''}` +
                `\nProgram: ${formatSecsOfDay(sched.start_sec)} for ${formatDuration(sched.duration_sec)}`
              : sched.runnable
              ? `\nArmed: ${formatSecsOfDay(sched.start_sec)} for ${formatDuration(sched.duration_sec)} (node clock)`
              : `\nNo program set`;

            // What the last tap has got as far as. Its own sentence, because
            // "the site says it did it" and "the node says the output moved"
            // are different facts and the operator has to be able to tell which
            // one is on screen.
            const cmdTitle = !cmd || !tile.note
              ? ''
              : cmd.detail
              ? `\n${cmd.detail}`
              : cmd.phase === 'acked'
              ? `\nThe site carried the command out. Reading the ${noun.oneLower}'s outputs back to confirm it…`
              : cmd.phase === 'confirming'
              ? `\nWaiting for the ${noun.oneLower} to report its outputs`
              : cmd.phase === 'confirmed'
              ? `\nThe ${noun.oneLower} reports this output ${cmd.want ? 'on' : 'off'}`
              : '';

            // A failed node still gets the normal instruction, prefixed with
            // what is known about it - the warning belongs alongside the
            // action, not instead of it.
            const failedPrefix = failed
              ? `${noun.one} did not answer the last command sent to it — worth trying anyway. `
              : '';
            const tooltip = (!controllerOnline
              ? 'Controller offline'
              : busy
              ? 'Sending command…'
              : failedPrefix + (intended
                ? `Click to turn off ${label}`
                : `Click to turn on ${label} — click again to stop it, or it stops itself after ${MANUAL_RUN_MAX_LABEL}`)) + cmdTitle + scheduleTitle;

            return (
              <button
                type="button"
                key={i}
                onClick={() => onToggleRelay(i, intended)}
                disabled={disabled}
                title={tooltip}
                className={`rounded-lg px-1.5 py-2 text-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  RELAY_TONE[tile.tone]
                } ${isMasterRelay ? 'border-2 border-amber-400' : ''} ${
                  wontRun && tile.tone === 'off' ? 'ring-1 ring-red-300' : ''
                } ${busy ? 'animate-pulse' : ''}`}
              >
                <div className="text-xs font-medium truncate" title={label}>{label.slice(0, 6)}</div>
                <div className={`text-xs mt-0.5 ${tile.tone === 'off' ? 'opacity-60' : 'opacity-90'}`}>
                  {tile.state}
                </div>
                {/* A tap in progress owns this line. What the schedule intends
                    can wait a minute; what the valve just did cannot. */}
                {(tile.note ?? scheduleNote) && (
                  <div
                    className={`text-[10px] mt-0.5 leading-tight truncate ${
                      tile.note
                        ? tile.tone === 'off' ? 'text-gray-500' : 'opacity-80'
                        : tile.tone !== 'off'
                        ? 'opacity-80'
                        : wontRun || bridge?.state === 'did-not-start'
                        ? 'text-red-600 font-medium'
                        : bridge?.state === 'due' || bridge?.state === 'skipped'
                        ? 'text-amber-600 font-medium'
                        : 'text-gray-400'
                    }`}
                  >
                    {tile.note ?? scheduleNote}
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-gray-400 italic">
          Tip: tap a relay to turn it on, tap it again to turn it off. Test runs
          auto-stop after 60 s.
        </p>
        {hiddenRelayCount > 0 && (
          <p className="text-[11px] text-gray-400">
            {hiddenRelayCount === 1 ? '1 relay is' : `${hiddenRelayCount} relays are`} marked as
            not in use and hidden — edit relay labels to change that.
          </p>
        )}

        {/* What the node itself last told us. Sourced from the device, so it
            stays true across reboots, NVS wipes and third-party masters. */}
        {schedule && (
          <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 text-[11px] text-gray-400 border-t border-gray-100 pt-2">
            <Clock className="w-3 h-3 flex-shrink-0" />
            <span className={schedule.clock_ok ? '' : 'text-red-500 font-medium'}>
              node clock{' '}
              {!schedule.clock_ok
                ? 'not set'
                : schedule.secs_today != null
                ? formatSecsOfDay(schedule.secs_today)
                : 'not reported'}
            </span>
            <span>·</span>
            <span>{armedCount} of {schedule.relay_count} armed</span>
            {schedule.reported_at && (
              <>
                <span>·</span>
                <span title={schedule.reported_at}>
                  reported {formatDateTimeInTz(schedule.reported_at, orgTz)}
                </span>
              </>
            )}
          </div>
        )}

        {/* Label editor */}
        {editingLabels ? (
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-gray-500">Edit Relay Labels</p>
            {!reportsOwnCount && (
              <div className="flex items-center gap-2">
                <label htmlFor={`relay-count-${router.router_mac}`} className="text-xs text-gray-400">
                  Relays on this board
                </label>
                <select
                  id={`relay-count-${router.router_mac}`}
                  value={countValue ?? ''}
                  onChange={e => setCountValue(e.target.value === '' ? null : parseInt(e.target.value))}
                  className="text-xs border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  {/* Blank = not set, so the transport default applies. */}
                  <option value="">Auto ({relayCountFor({ transport: router.transport })})</option>
                  {[1, 2, 3, 4, 6, 8].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            )}
            {/* "In use" is a separate question from how many relays the board
                has, and both have to be askable. A board wired on 0 and 3 must
                still say it has 4 - dropping the count to 2 would make channel
                3 unaddressable - so the unused ones are ticked off instead, and
                every picker stops offering them. */}
            <p className="text-[11px] text-gray-400">
              Untick a relay that is not wired to anything. It stops appearing in
              the schedule and pump pickers, but keeps its channel number.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {Array.from({ length: editRelayCount }, (_, i) => {
                const inUse = !disabledValue.has(i);
                return (
                  <div key={i} className="flex items-center gap-1.5">
                    <input
                      type="checkbox"
                      id={`relay-in-use-${router.router_mac}-${i}`}
                      checked={inUse}
                      onChange={e => setDisabledValue(prev => {
                        const next = new Set(prev);
                        if (e.target.checked) next.delete(i); else next.add(i);
                        return next;
                      })}
                      className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
                    />
                    <label
                      htmlFor={`relay-in-use-${router.router_mac}-${i}`}
                      className="text-xs text-gray-400 w-12 flex-shrink-0 cursor-pointer"
                      title={inUse ? `Relay ${i} is in use` : `Relay ${i} is not wired to anything`}
                    >
                      Relay {i}
                    </label>
                    <input
                      type="text"
                      maxLength={20}
                      placeholder={inUse ? `Relay ${i}` : 'Not in use'}
                      disabled={!inUse}
                      value={labels[String(i)] ?? ''}
                      onChange={e => setLabels(prev => ({ ...prev, [String(i)]: e.target.value }))}
                      className="flex-1 min-w-0 text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex gap-2">
              <button onClick={saveLabels} className="text-xs text-blue-600 hover:underline">Save Labels</button>
              <button onClick={() => setEditingLabels(false)} className="text-xs text-gray-400 hover:underline">Cancel</button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setEditingLabels(true)}
            className="text-xs text-blue-600 hover:underline"
          >
            <Settings className="w-3 h-3 inline mr-0.5" /> Edit relay labels
          </button>
        )}

        {/* One node, asked on its own. XBee only, for the same reason the
            diagnostics panel is: the endpoint refuses anything else, and an
            ESP-NOW master would ack it as an unsupported command a minute
            later. Hidden for the master's own on-board relay bank too — there
            is no radio link between the controller and itself to measure. */}
        {transport === 'xbee' && !router.is_local && (
          <NodeSelfTest
            controllerId={controllerId}
            router={router}
            transport={transport}
            controllerOnline={controllerOnline}
            onFinished={onTestFinished}
          />
        )}
      </CardContent>
    </Card>
  );
}

// ─── Commands Table ───────────────────────────────────────────────────────────

/**
 * What we asked this site to do, and what it said back.
 *
 * Every ack the hardware sends has always been stored — status, detail,
 * status_code, armed — and until now none of it was readable anywhere. The only
 * consumer was the schedule push's ack poll, which aggregates, keeps just the
 * failures, and runs for sixty seconds after a push and never again. So a
 * command that failed at any other moment left a precise explanation in the
 * database that no screen would ever show.
 *
 * The case that forced this: a forced run on a bridge predating RUN_PROGRAM was
 * acked `unsupported command type 'RUN_PROGRAM'` — exactly the sentence needed
 * to diagnose it — while the operator watched a site claim to be watering and
 * do nothing.
 */
function CommandsTable({ commands, routers }: { commands: FieldCommand[]; routers: EspNowRouter[] }) {
  const orgTz = useOrgTimezone();

  const statusStyle: Record<string, { chip: string; label: string }> = {
    QUEUED:     { chip: 'bg-gray-100 text-gray-600',     label: 'Queued' },
    DISPATCHED: { chip: 'bg-blue-100 text-blue-700',     label: 'Collected' },
    PENDING:    { chip: 'bg-amber-100 text-amber-700',   label: 'Pending' },
    OK:         { chip: 'bg-green-100 text-green-700',   label: 'OK' },
    CONFIRMED:  { chip: 'bg-green-100 text-green-700',   label: 'Confirmed' },
    ERROR:      { chip: 'bg-red-100 text-red-700',       label: 'Failed' },
  };

  function routerLabel(mac: unknown): string | null {
    if (typeof mac !== 'string' || !mac) return null;
    const r = routers.find(x => x.router_mac.toUpperCase() === mac.toUpperCase());
    return r?.name || mac.slice(-6);
  }

  /** The one line of context that says which command this was. */
  function target(cmd: FieldCommand): string {
    const p = cmd.payload || {};
    const node = routerLabel(p.router_mac);
    const relay = p.relay ?? p.relay_index;
    if (node && relay != null) return `${node} · relay ${relay}`;
    if (node) return node;
    if (typeof p.version === 'string') return `v${p.version}`;
    // A program id is not something anyone can read. Saying so is more use than
    // showing 36 characters of UUID.
    if (p.program_id) return 'schedule program';
    return '—';
  }

  if (commands.length === 0) {
    return (
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Commands sent</h3>
        <div className="flex items-center justify-center h-24 text-gray-400 text-sm">
          Nothing has been sent to this controller yet.
        </div>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-700 mb-1">Commands sent</h3>
      <p className="text-xs text-gray-500 mb-3">
        What the site was asked to do, and what it answered. A failure here is
        usually the whole explanation for something that did not happen.
      </p>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
              <th className="py-2 pr-4 font-medium">Sent</th>
              <th className="py-2 pr-4 font-medium">Command</th>
              <th className="py-2 pr-4 font-medium">Target</th>
              <th className="py-2 pr-4 font-medium">Result</th>
              <th className="py-2 font-medium">What it said</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {commands.map(cmd => {
              const style = statusStyle[cmd.status] || { chip: 'bg-gray-100 text-gray-600', label: cmd.status };
              const failed = cmd.status === 'ERROR';
              // Stored but inert. A success by the ack's reckoning and a dry
              // block by any other, so it must not read as a plain OK.
              const inert = cmd.armed === false;
              return (
                <tr key={cmd.cmd_id} className={failed ? 'bg-red-50/40' : undefined}>
                  <td className="py-2 pr-4 whitespace-nowrap text-gray-500">
                    {formatDateTimeInTz(cmd.created_at, orgTz)}
                  </td>
                  <td className="py-2 pr-4 whitespace-nowrap font-mono text-xs text-gray-700">
                    {cmd.type}
                  </td>
                  <td className="py-2 pr-4 whitespace-nowrap text-gray-600">{target(cmd)}</td>
                  <td className="py-2 pr-4 whitespace-nowrap">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${style.chip}`}>
                      {style.label}
                    </span>
                    {inert && (
                      <span className="ml-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                        not armed
                      </span>
                    )}
                  </td>
                  <td className={`py-2 ${failed ? 'text-red-600 font-medium' : inert ? 'text-amber-700' : 'text-gray-500'}`}>
                    {cmd.detail || (cmd.status === 'QUEUED' ? 'Not collected yet' : '—')}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Events Table ─────────────────────────────────────────────────────────────

function EventsTable({ events, routers, transport }: { events: EspNowEvent[]; routers: EspNowRouter[]; transport?: string | null }) {
  const orgTz = useOrgTimezone();
  const noun = nodeNoun(transport);
  const typeColour: Record<string, string> = {
    ANNOUNCE:       'bg-green-100 text-green-700',
    HEARTBEAT_ACK:  'bg-blue-100 text-blue-700',
    OFFLINE:        'bg-red-100 text-red-700',
    CONFIG_ACK:     'bg-purple-100 text-purple-700',
    OTA_ACK:        'bg-indigo-100 text-indigo-700',
    SCHEDULE_STATE: 'bg-teal-100 text-teal-700',
    // The XBee bridge's own events. A sequence that did not run is the most
    // consequential line this table can show - water that did not happen, on a
    // farm nobody is standing in - and it was rendering in the same grey as a
    // heartbeat.
    SEQUENCE_SKIPPED:  'bg-red-100 text-red-700',
    VALVE_FAILURE:     'bg-red-100 text-red-700',
    NO_XBEE_RESPONSE:  'bg-red-100 text-red-700',
    PREFLIGHT_WARNING: 'bg-amber-100 text-amber-700',
    CLOCK_UNTRUSTED:   'bg-amber-100 text-amber-700',
    PROGRAM_STARTED:   'bg-blue-100 text-blue-700',
    SEQUENCE_STARTED:  'bg-blue-100 text-blue-700',
    PROGRAM_COMPLETED: 'bg-green-100 text-green-700',
    COORDINATOR_OK:    'bg-green-100 text-green-700',
    NODE_CANDIDATE:    'bg-amber-100 text-amber-700',
  };

  function routerLabel(mac: string | null): string {
    if (!mac) return '—';
    const r = routers.find(x => x.router_mac === mac);
    return r?.name || mac;
  }

  /**
   * A CONFIG_ACK carrying `status: "OK"` is not a promise that the program will
   * water — codes 3 and 4 store the schedule but leave it unarmed. Read the ack
   * into plain language so an inert program cannot pass as a success.
   */
  function ackSummary(ev: EspNowEvent): { text: string; tone: 'ok' | 'warn' | 'error' } | null {
    if (ev.event_type === 'OTA_ACK') {
      const status = String(ev.payload.status ?? '');
      if (status === 'STARTING') return { text: 'Downloading firmware', tone: 'ok' };
      if (status === 'FAILED') return { text: 'Mesh delivery failed', tone: 'error' };
      return status ? { text: status, tone: 'warn' } : null;
    }
    // Anything from the bridge explains itself in `detail`. Showing it is the
    // difference between "SEQUENCE_SKIPPED" and "Sequence 2 of Block A skipped:
    // 1 of 3 nodes did not answer (0013A2...)".
    const detail = ev.payload?.detail;
    if (typeof detail === 'string' && detail) {
      const tone: 'ok' | 'warn' | 'error' =
        ev.event_type === 'SEQUENCE_SKIPPED' || ev.event_type === 'VALVE_FAILURE'
        || ev.event_type === 'NO_XBEE_RESPONSE'
          ? 'error'
          : ev.event_type === 'PREFLIGHT_WARNING' || ev.event_type === 'CLOCK_UNTRUSTED'
            ? 'warn'
            : 'ok';
      return { tone, text: detail };
    }
    if (ev.event_type !== 'CONFIG_ACK') return null;
    const p = ev.payload as ConfigAckPayload;
    const known = p.status_code != null ? CONFIG_ACK_STATUS[p.status_code] : undefined;
    const relay = p.relay_id != null ? `Relay ${p.relay_id}: ` : '';
    if (known) return { text: relay + known.label, tone: known.tone };
    // Older firmware without status_code — fall back to armed/status/detail.
    if (p.armed === false) {
      return { text: relay + (p.detail || 'stored, but not armed — will not run'), tone: 'warn' };
    }
    if (p.status === 'ERROR') return { text: relay + (p.detail || 'rejected'), tone: 'error' };
    if (p.status === 'OK') return { text: relay + 'armed', tone: 'ok' };
    return null;
  }

  const ackTone: Record<'ok' | 'warn' | 'error', string> = {
    ok:    'text-green-600',
    warn:  'text-amber-600 font-medium',
    error: 'text-red-600 font-medium',
  };

  if (events.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-gray-400 text-sm">
        No events recorded yet.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wide">
            <th className="text-left py-2 pr-4">Time</th>
            <th className="text-left py-2 pr-4">Type</th>
            <th className="text-left py-2 pr-4 hidden sm:table-cell">{noun.one}</th>
            <th className="text-left py-2 pr-4">Result</th>
            <th className="text-left py-2 hidden lg:table-cell">Payload</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {events.map(ev => {
            const ack = ackSummary(ev);
            return (
              <tr key={ev.event_id} className="hover:bg-gray-50">
                <td className="py-2 pr-4 text-xs text-gray-500 whitespace-nowrap">
                  {formatDateTimeInTz(ev.timestamp, orgTz)}
                </td>
                <td className="py-2 pr-4">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${typeColour[ev.event_type] ?? 'bg-gray-100 text-gray-600'}`}>
                    {ev.event_type}
                  </span>
                </td>
                <td className="py-2 pr-4 text-xs text-gray-500 hidden sm:table-cell" title={ev.router_mac ?? undefined}>
                  {routerLabel(ev.router_mac)}
                </td>
                <td className="py-2 pr-4 text-xs">
                  {ack
                    ? <span className={ackTone[ack.tone]}>{ack.text}</span>
                    : <span className="text-gray-300">—</span>}
                </td>
                <td className="py-2 text-xs text-gray-500 max-w-xs truncate font-mono hidden lg:table-cell">
                  {JSON.stringify(ev.payload)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Need React import for JSX in type annotation
import React from 'react';
